const foodTopics = [
  // ─────────────────────────────────────────────
  // 1. Protein
  // ─────────────────────────────────────────────
  {
    slug: 'protein',
    title: 'Protein',
    synonyms: ['amino acids', 'complete protein', 'dietary protein'],
    whatItIs:
      'Protein is a macronutrient made up of amino acids that serves as the primary building block for muscle, bone, skin, and many enzymes and hormones.',
    whyItMatters:
      'Adequate protein intake is essential for maintaining lean muscle mass, supporting immune function, and enabling tissue repair. Evidence suggests that higher-protein diets are associated with improved satiety and may support healthy body composition, particularly during weight loss or aging (ref 1, 2).',
    sources: [
      'Chicken breast',
      'Eggs',
      'Greek yoghurt',
      'Lentils',
      'Salmon',
      'Tofu',
      'Cottage cheese',
      'Lean beef',
      'Chickpeas',
      'Quinoa',
    ],
    howMuch:
      'The RDA is 0.8 g per kg of body weight per day for sedentary adults (ref 1). Current evidence suggests active individuals and older adults may benefit from 1.2-2.0 g/kg/day (ref 2). Requirements vary by age, sex, activity level, and health status.',
    deficiencyExcess:
      'Deficiency signs include muscle wasting, edema, weakened immunity, and slow wound healing. Excess protein intake over prolonged periods may stress the kidneys in individuals with pre-existing kidney disease, though evidence in healthy adults is limited (ref 3).',
    ageNotes:
      'Older adults (65+) may benefit from higher intake (1.0-1.2 g/kg/day) due to anabolic resistance and age-related muscle loss (sarcopenia) (ref 2).',
    myths: [
      {
        myth: 'High protein intake damages healthy kidneys.',
        fact: 'A 2018 meta-analysis found no adverse effects of high protein diets on kidney function in individuals without pre-existing renal disease (ref 3).',
      },
      {
        myth: 'You can only absorb 30 g of protein per meal.',
        fact: 'The body can digest and absorb well above 30 g per meal; however, muscle protein synthesis per meal may plateau around 0.4-0.55 g/kg, suggesting spreading intake across meals is beneficial (ref 4).',
      },
      {
        myth: 'Plant proteins are always incomplete and inferior.',
        fact: 'While some plant proteins are lower in certain amino acids, combining different plant sources throughout the day easily provides all essential amino acids (ref 5).',
      },
    ],
    relatedTopics: [
      'muscle-recovery',
      'strength-training',
      'protein-timing',
      'hair-health',
      'bone-density',
    ],
    references: [
      {
        id: 1,
        text: 'Institute of Medicine. Dietary Reference Intakes for Energy, Carbohydrate, Fiber, Fat, Fatty Acids, Cholesterol, Protein, and Amino Acids. National Academies Press, 2005.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/12449285/',
      },
      {
        id: 2,
        text: 'Bauer J, et al. Evidence-based recommendations for optimal dietary protein intake in older people: a position paper from the PROT-AGE Study Group. J Am Med Dir Assoc. 2013;14(8):542-559.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/23867520/',
      },
      {
        id: 3,
        text: 'Devries MC, et al. Changes in Kidney Function Do Not Differ between Healthy Adults Consuming Higher- Compared with Lower- or Normal-Protein Diets: A Systematic Review and Meta-Analysis. J Nutr. 2018;148(11):1760-1775.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/30383278/',
      },
      {
        id: 4,
        text: 'Schoenfeld BJ, Aragon AA. How much protein can the body use in a single meal for muscle-building? Implications for daily protein distribution. J Int Soc Sports Nutr. 2018;15:10.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/29497353/',
      },
      {
        id: 5,
        text: 'Young VR, Pellett PL. Plant proteins in relation to human protein and amino acid nutrition. Am J Clin Nutr. 1994;59(5 Suppl):1203S-1212S.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/8172124/',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 2. Fibre
  // ─────────────────────────────────────────────
  {
    slug: 'fibre',
    title: 'Fibre',
    synonyms: ['fiber', 'roughage', 'dietary fibre', 'dietary fiber'],
    whatItIs:
      'Fibre is the indigestible portion of plant foods that passes through the digestive system largely intact, classified as soluble (dissolves in water) or insoluble.',
    whyItMatters:
      'Dietary fibre is associated with reduced risk of cardiovascular disease, type 2 diabetes, and colorectal cancer. It supports gut health by feeding beneficial bacteria, promotes regular bowel movements, and may help manage blood sugar and cholesterol levels (ref 1, 2).',
    sources: [
      'Oats',
      'Lentils',
      'Black beans',
      'Broccoli',
      'Chia seeds',
      'Psyllium husk',
      'Apples',
      'Sweet potatoes',
      'Flaxseeds',
      'Whole-grain bread',
    ],
    howMuch:
      'Current guidelines recommend 25 g/day for women and 38 g/day for men (ages 19-50). Over 50, recommendations drop to 21 g/day and 30 g/day respectively (ref 3). Most adults consume only about 15 g/day on average [VERIFY].',
    deficiencyExcess:
      'Low fibre intake is associated with constipation, elevated cholesterol, and increased colorectal cancer risk. Rapidly increasing fibre without adequate hydration can cause bloating, gas, and cramping.',
    ageNotes:
      'Older adults should prioritise soluble fibre sources for heart and blood sugar benefits, and increase intake gradually to minimise digestive discomfort.',
    myths: [
      {
        myth: 'All fibre is the same.',
        fact: 'Soluble fibre (oats, beans) helps lower cholesterol and regulate blood sugar, while insoluble fibre (wheat bran, vegetables) primarily supports bowel regularity (ref 1).',
      },
      {
        myth: 'Fibre supplements are just as good as fibre from food.',
        fact: 'Whole-food sources provide additional vitamins, minerals, and phytonutrients that supplements lack. Evidence suggests whole-food fibre is more strongly associated with health benefits (ref 2).',
      },
    ],
    relatedTopics: ['gut-health', 'cholesterol', 'carbs', 'inflammation'],
    references: [
      {
        id: 1,
        text: 'Slavin JL. Dietary fiber and body weight. Nutrition. 2005;21(3):411-418.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/15797686/',
      },
      {
        id: 2,
        text: 'Reynolds A, et al. Carbohydrate quality and human health: a series of systematic reviews and meta-analyses. Lancet. 2019;393(10170):434-445.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/30638909/',
      },
      {
        id: 3,
        text: 'Institute of Medicine. Dietary Reference Intakes for Energy, Carbohydrate, Fiber, Fat, Fatty Acids, Cholesterol, Protein, and Amino Acids. National Academies Press, 2005.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/12449285/',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 3. Carbs
  // ─────────────────────────────────────────────
  {
    slug: 'carbs',
    title: 'Carbohydrates',
    synonyms: ['carbohydrates', 'glucose', 'glycogen', 'carbs', 'starch'],
    whatItIs:
      'Carbohydrates are the body\'s primary energy source, broken down into glucose to fuel cells, tissues, and organs. They range from simple sugars to complex starches and fibre.',
    whyItMatters:
      'Carbohydrates fuel brain function, exercise performance, and daily activity. The quality of carbohydrate sources matters significantly: whole, minimally processed carbs with fibre are associated with better metabolic outcomes, while refined carbs and added sugars are linked to increased risk of obesity, type 2 diabetes, and cardiovascular disease (ref 1, 2).',
    sources: [
      'Sweet potatoes',
      'Brown rice',
      'Oats',
      'Quinoa',
      'Bananas',
      'Legumes',
      'Whole-grain pasta',
      'Berries',
      'Barley',
      'Butternut squash',
    ],
    howMuch:
      'The RDA for carbohydrates is 130 g/day based on average minimum glucose utilisation by the brain (ref 3). Typical recommendations range from 45-65% of total calories. Athletes may require more depending on training volume and intensity.',
    deficiencyExcess:
      'Very low carb intake can cause fatigue, brain fog, irritability, and constipation. Excess refined carbohydrate consumption is associated with weight gain, insulin resistance, and elevated triglycerides.',
    ageNotes:
      'Older adults may benefit from choosing lower-glycaemic-index carbohydrate sources to help manage blood sugar and reduce insulin resistance risk.',
    myths: [
      {
        myth: 'Carbs make you fat.',
        fact: 'Weight gain results from chronic caloric surplus, not carbohydrates per se. Whole-food carbohydrate sources are associated with healthy weight maintenance (ref 2).',
      },
      {
        myth: 'Low-carb diets are always superior for weight loss.',
        fact: 'Meta-analyses show that when protein and calories are matched, low-carb and low-fat diets produce similar weight loss over 12 months (ref 4).',
      },
    ],
    relatedTopics: ['fibre', 'sugar', 'metabolism', 'inflammation', 'gut-health'],
    references: [
      {
        id: 1,
        text: 'Ludwig DS. The glycemic index: physiological mechanisms relating to obesity, diabetes, and cardiovascular disease. JAMA. 2002;287(18):2414-2423.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/11988062/',
      },
      {
        id: 2,
        text: 'Reynolds A, et al. Carbohydrate quality and human health: a series of systematic reviews and meta-analyses. Lancet. 2019;393(10170):434-445.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/30638909/',
      },
      {
        id: 3,
        text: 'Institute of Medicine. Dietary Reference Intakes for Energy, Carbohydrate, Fiber, Fat, Fatty Acids, Cholesterol, Protein, and Amino Acids. National Academies Press, 2005.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/12449285/',
      },
      {
        id: 4,
        text: 'Hall KD, Guo J. Obesity Energetics: Body Weight Regulation and the Effects of Diet Composition. Gastroenterology. 2017;152(7):1718-1727.e3.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/28193517/',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 4. Good Fats
  // ─────────────────────────────────────────────
  {
    slug: 'good-fats',
    title: 'Good Fats',
    synonyms: [
      'healthy fats',
      'unsaturated fats',
      'monounsaturated',
      'polyunsaturated',
      'MUFA',
      'PUFA',
    ],
    whatItIs:
      'Good fats refer primarily to unsaturated fatty acids -- monounsaturated (MUFAs) and polyunsaturated (PUFAs) -- that support cardiovascular and brain health when consumed in place of saturated and trans fats.',
    whyItMatters:
      'Replacing saturated fats with unsaturated fats is associated with reduced LDL cholesterol and lower risk of cardiovascular events. MUFAs and PUFAs also support cell membrane integrity, hormone production, and absorption of fat-soluble vitamins (A, D, E, K) (ref 1, 2).',
    sources: [
      'Extra-virgin olive oil',
      'Avocados',
      'Almonds',
      'Walnuts',
      'Salmon',
      'Mackerel',
      'Chia seeds',
      'Flaxseeds',
      'Peanut butter (natural)',
      'Sunflower seeds',
    ],
    howMuch:
      'The AHA recommends that total fat comprise 25-35% of daily calories, with emphasis on unsaturated sources. Limit saturated fat to less than 10% of calories and avoid artificial trans fats entirely (ref 2). Specific PUFA targets are discussed under omega-3.',
    deficiencyExcess:
      'Very low fat diets can impair hormone production, reduce absorption of fat-soluble vitamins, and affect skin and hair health. Excessive fat intake of any kind contributes to caloric surplus and weight gain.',
    ageNotes:
      'Older adults should prioritise omega-3-rich sources for their anti-inflammatory and cardioprotective properties.',
    myths: [
      {
        myth: 'All fat is bad for you.',
        fact: 'Decades of research confirm that the type of fat matters more than the total amount. Unsaturated fats from whole-food sources are associated with improved cardiovascular outcomes (ref 1).',
      },
      {
        myth: 'Eating fat makes you fat.',
        fact: 'Fat is calorie-dense (9 kcal/g) but also highly satiating. In controlled trials, moderate-fat diets do not cause more weight gain than low-fat diets when calories are matched (ref 3).',
      },
    ],
    relatedTopics: ['omega-3', 'cholesterol', 'inflammation', 'belly-fat', 'skin-health'],
    references: [
      {
        id: 1,
        text: 'Sacks FM, et al. Dietary Fats and Cardiovascular Disease: A Presidential Advisory From the American Heart Association. Circulation. 2017;136(3):e1-e23.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/28620111/',
      },
      {
        id: 2,
        text: 'American Heart Association. Dietary Fats. heart.org.',
        url: 'https://www.heart.org/en/healthy-living/healthy-eating/eat-smart/fats/dietary-fats',
      },
      {
        id: 3,
        text: 'Tobias DK, et al. Effect of low-fat diet interventions versus other diet interventions on long-term weight change in adults: a systematic review and meta-analysis. Lancet Diabetes Endocrinol. 2015;3(12):968-979.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/26527511/',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 5. Omega-3
  // ─────────────────────────────────────────────
  {
    slug: 'omega-3',
    title: 'Omega-3 Fatty Acids',
    synonyms: ['fish oil', 'EPA', 'DHA', 'ALA', 'omega 3'],
    whatItIs:
      'Omega-3 fatty acids are essential polyunsaturated fats that the body cannot produce on its own. The three main types are ALA (alpha-linolenic acid, from plants), EPA (eicosapentaenoic acid), and DHA (docosahexaenoic acid, both primarily from marine sources).',
    whyItMatters:
      'EPA and DHA are associated with reduced inflammation, lower triglycerides, and decreased risk of cardiovascular disease. DHA is a critical structural component of the brain and retina. Evidence suggests omega-3 intake may support cognitive function and mental health (ref 1, 2).',
    sources: [
      'Salmon',
      'Mackerel',
      'Sardines',
      'Anchovies',
      'Herring',
      'Walnuts',
      'Flaxseeds',
      'Chia seeds',
      'Hemp seeds',
      'Algae oil (vegan DHA/EPA)',
    ],
    howMuch:
      'The AHA recommends at least two servings of fatty fish per week (approx. 250-500 mg combined EPA+DHA per day) (ref 3). ALA adequate intake is 1.6 g/day for men and 1.1 g/day for women. Conversion of ALA to EPA/DHA is limited (typically <10%) [VERIFY].',
    deficiencyExcess:
      'Deficiency is associated with dry skin, poor concentration, joint stiffness, and increased inflammation. Very high doses (>3 g/day from supplements) may increase bleeding risk and should be supervised by a healthcare provider (ref 3).',
    ageNotes:
      'DHA is critical during pregnancy and early childhood for brain and eye development. Older adults may benefit from EPA/DHA for cardiovascular and cognitive support.',
    myths: [
      {
        myth: 'Flaxseed gives you all the omega-3 you need.',
        fact: 'Flaxseed provides ALA, but conversion to the more bioactive EPA and DHA is very limited. Direct sources of EPA/DHA (fish or algae) are more effective (ref 4).',
      },
      {
        myth: 'Fish oil supplements are always necessary.',
        fact: 'Two servings of fatty fish per week generally meets EPA/DHA needs for most people. Supplements are most beneficial for those who do not eat fish regularly (ref 3).',
      },
    ],
    relatedTopics: [
      'good-fats',
      'inflammation',
      'cholesterol',
      'skin-health',
      'hair-health',
      'anti-aging',
    ],
    references: [
      {
        id: 1,
        text: 'Calder PC. Omega-3 fatty acids and inflammatory processes: from molecules to man. Biochem Soc Trans. 2017;45(5):1105-1115.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/28900017/',
      },
      {
        id: 2,
        text: 'Grosso G, et al. Role of omega-3 fatty acids in the treatment of depressive disorders: a comprehensive meta-analysis of randomized clinical trials. PLoS One. 2014;9(5):e96905.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/24805797/',
      },
      {
        id: 3,
        text: 'Rimm EB, et al. Seafood Long-Chain n-3 Polyunsaturated Fatty Acids and Cardiovascular Disease: A Science Advisory From the American Heart Association. Circulation. 2018;138(1):e35-e47.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/29773586/',
      },
      {
        id: 4,
        text: 'Burdge GC, Calder PC. Conversion of alpha-linolenic acid to longer-chain polyunsaturated fatty acids in human adults. Reprod Nutr Dev. 2005;45(5):581-597.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/16188209/',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 6. Vitamin C
  // ─────────────────────────────────────────────
  {
    slug: 'vitamin-c',
    title: 'Vitamin C',
    synonyms: ['ascorbic acid', 'vit c', 'vitamin c'],
    whatItIs:
      'Vitamin C is a water-soluble vitamin and potent antioxidant that the body cannot synthesise, making dietary intake essential.',
    whyItMatters:
      'Vitamin C is required for collagen synthesis, wound healing, and immune function. It enhances non-haeme iron absorption and acts as an antioxidant, protecting cells from oxidative damage. Evidence suggests adequate vitamin C intake is associated with reduced risk of chronic diseases (ref 1, 2).',
    sources: [
      'Red bell peppers',
      'Oranges',
      'Kiwi fruit',
      'Strawberries',
      'Broccoli',
      'Brussels sprouts',
      'Guava',
      'Papaya',
      'Tomatoes',
      'Kale',
    ],
    howMuch:
      'The RDA is 90 mg/day for adult men and 75 mg/day for adult women. Smokers need an additional 35 mg/day. The upper tolerable limit is 2,000 mg/day (ref 3). Most people can meet requirements through diet alone.',
    deficiencyExcess:
      'Severe deficiency causes scurvy (bleeding gums, bruising, poor wound healing, fatigue). Excess intake (>2,000 mg/day) may cause diarrhoea, nausea, and kidney stones in susceptible individuals (ref 3).',
    ageNotes:
      'Requirements are the same for older adults, but vitamin C supports collagen integrity and immune function, both of which decline with age.',
    myths: [
      {
        myth: 'Megadoses of vitamin C prevent the common cold.',
        fact: 'A Cochrane review found that regular vitamin C supplementation does not prevent colds in the general population, though it may modestly reduce duration and severity (ref 4).',
      },
      {
        myth: 'Oranges are the best source of vitamin C.',
        fact: 'While oranges are good sources, red bell peppers, guava, and kiwi fruit contain significantly more vitamin C per serving (ref 3).',
      },
    ],
    relatedTopics: ['iron', 'skin-health', 'inflammation', 'anti-aging', 'hair-health'],
    references: [
      {
        id: 1,
        text: 'Carr AC, Maggini S. Vitamin C and Immune Function. Nutrients. 2017;9(11):1211.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/29099763/',
      },
      {
        id: 2,
        text: 'Pullar JM, Carr AC, Vissers MCM. The Roles of Vitamin C in Skin Health. Nutrients. 2017;9(8):866.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/28805671/',
      },
      {
        id: 3,
        text: 'NIH Office of Dietary Supplements. Vitamin C Fact Sheet for Health Professionals.',
        url: 'https://ods.od.nih.gov/factsheets/VitaminC-HealthProfessional/',
      },
      {
        id: 4,
        text: 'Hemila H, Chalker E. Vitamin C for preventing and treating the common cold. Cochrane Database Syst Rev. 2013;(1):CD000980.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/23440782/',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 7. Vitamin K
  // ─────────────────────────────────────────────
  {
    slug: 'vitamin-k',
    title: 'Vitamin K',
    synonyms: ['vit k', 'phylloquinone', 'menaquinone', 'vitamin k1', 'vitamin k2'],
    whatItIs:
      'Vitamin K is a fat-soluble vitamin that exists in two main forms: K1 (phylloquinone, from plants) and K2 (menaquinone, from fermented foods and animal products). It is essential for blood clotting and bone metabolism.',
    whyItMatters:
      'Vitamin K activates proteins required for blood coagulation and is critical for directing calcium into bones rather than arteries. Evidence suggests that adequate vitamin K2 intake is associated with reduced arterial calcification and improved bone density (ref 1, 2).',
    sources: [
      'Kale',
      'Spinach',
      'Collard greens',
      'Broccoli',
      'Brussels sprouts',
      'Natto (fermented soybeans)',
      'Cheese (particularly Gouda and Brie)',
      'Egg yolks',
      'Chicken liver',
      'Fermented foods',
    ],
    howMuch:
      'The adequate intake (AI) is 120 mcg/day for adult men and 90 mcg/day for adult women (ref 3). No upper tolerable limit has been established for natural vitamin K. People on anticoagulant medication (e.g., warfarin) must maintain consistent vitamin K intake.',
    deficiencyExcess:
      'Deficiency is uncommon in adults but can cause excessive bleeding and poor blood clotting. It is more common in newborns (hence the vitamin K injection at birth). Subclinical deficiency may contribute to arterial calcification and reduced bone density.',
    ageNotes:
      'Older adults may benefit from ensuring adequate K2 intake for bone health and vascular calcification prevention. Those on blood-thinning medications should consult their healthcare provider.',
    myths: [
      {
        myth: 'Vitamin K1 and K2 are interchangeable.',
        fact: 'K1 primarily supports blood clotting, while K2 plays a more significant role in calcium metabolism and bone/cardiovascular health. They have different tissue distributions and functions (ref 2).',
      },
      {
        myth: 'You need to avoid all vitamin K if you take blood thinners.',
        fact: 'The key is consistency, not avoidance. Sudden changes in vitamin K intake can affect anticoagulant medication efficacy, but a steady intake can be managed with appropriate medication dosing (ref 3).',
      },
    ],
    relatedTopics: ['bone-density', 'calcium', 'cholesterol', 'vitamin-a'],
    references: [
      {
        id: 1,
        text: 'Geleijnse JM, et al. Dietary intake of menaquinone is associated with a reduced risk of coronary heart disease: the Rotterdam Study. J Nutr. 2004;134(11):3100-3105.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/15514282/',
      },
      {
        id: 2,
        text: 'Schwalfenberg GK. Vitamins K1 and K2: The Emerging Group of Vitamins Required for Human Health. J Nutr Metab. 2017;2017:6254836.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/28698808/',
      },
      {
        id: 3,
        text: 'NIH Office of Dietary Supplements. Vitamin K Fact Sheet for Health Professionals.',
        url: 'https://ods.od.nih.gov/factsheets/VitaminK-HealthProfessional/',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 8. Vitamin A
  // ─────────────────────────────────────────────
  {
    slug: 'vitamin-a',
    title: 'Vitamin A',
    synonyms: ['retinol', 'beta-carotene', 'vit a', 'retinoids', 'carotenoids'],
    whatItIs:
      'Vitamin A is a fat-soluble vitamin found as preformed retinol (in animal foods) and as provitamin A carotenoids like beta-carotene (in plants). It is essential for vision, immune function, and cell growth.',
    whyItMatters:
      'Vitamin A is critical for maintaining healthy vision (especially night vision), supporting immune defence, and enabling normal cell differentiation. Beta-carotene also acts as an antioxidant. Deficiency remains a leading cause of preventable blindness worldwide (ref 1, 2).',
    sources: [
      'Sweet potatoes',
      'Carrots',
      'Spinach',
      'Kale',
      'Liver (beef or chicken)',
      'Eggs',
      'Cantaloupe',
      'Red bell peppers',
      'Mangoes',
      'Butternut squash',
    ],
    howMuch:
      'The RDA is 900 mcg RAE/day for adult men and 700 mcg RAE/day for adult women. The upper tolerable limit for preformed vitamin A is 3,000 mcg RAE/day; excessive intake can be toxic. Beta-carotene from food does not carry the same toxicity risk (ref 3).',
    deficiencyExcess:
      'Deficiency causes night blindness, dry eyes, impaired immunity, and rough skin. Excess preformed vitamin A (hypervitaminosis A) can cause nausea, headaches, liver damage, and is teratogenic during pregnancy (ref 3).',
    ageNotes:
      'Older adults should ensure adequate intake for immune and eye health, but should avoid high-dose preformed vitamin A supplements due to liver toxicity risk.',
    myths: [
      {
        myth: 'Eating carrots gives you perfect vision.',
        fact: 'Carrots provide beta-carotene which supports eye health and prevents deficiency-related vision loss, but they cannot correct refractive errors or improve vision beyond normal capacity (ref 1).',
      },
      {
        myth: 'More vitamin A is always better.',
        fact: 'Preformed vitamin A is stored in the liver and can reach toxic levels. Unlike water-soluble vitamins, excess is not simply excreted (ref 3).',
      },
    ],
    relatedTopics: ['skin-health', 'vitamin-c', 'vitamin-k', 'anti-aging', 'hair-health'],
    references: [
      {
        id: 1,
        text: 'WHO. Vitamin A deficiency. Global Health Observatory.',
        url: 'https://www.who.int/data/nutrition/nlis/info/vitamin-a-deficiency',
      },
      {
        id: 2,
        text: 'Huang Z, et al. Role of Vitamin A in the Immune System. J Clin Med. 2018;7(9):258.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/30200565/',
      },
      {
        id: 3,
        text: 'NIH Office of Dietary Supplements. Vitamin A and Carotenoids Fact Sheet for Health Professionals.',
        url: 'https://ods.od.nih.gov/factsheets/VitaminA-HealthProfessional/',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 9. Vitamin B12
  // ─────────────────────────────────────────────
  {
    slug: 'vitamin-b12',
    title: 'Vitamin B12',
    synonyms: ['cobalamin', 'vit b12', 'b12', 'cyanocobalamin', 'methylcobalamin'],
    whatItIs:
      'Vitamin B12 is a water-soluble vitamin essential for red blood cell formation, neurological function, and DNA synthesis. It is naturally found almost exclusively in animal-derived foods.',
    whyItMatters:
      'B12 deficiency can cause megaloblastic anaemia, neurological damage (including peripheral neuropathy and cognitive decline), and fatigue. Because the body stores several years\' worth, deficiency develops slowly but can become irreversible if neurological damage occurs (ref 1, 2).',
    sources: [
      'Liver (beef)',
      'Clams',
      'Salmon',
      'Tuna',
      'Beef',
      'Eggs',
      'Dairy products',
      'Nutritional yeast (fortified)',
      'Fortified plant milks',
      'Fortified cereals',
    ],
    howMuch:
      'The RDA is 2.4 mcg/day for adults. Pregnant women need 2.6 mcg/day and lactating women 2.8 mcg/day (ref 3). Vegans and older adults may need supplementation or fortified foods.',
    deficiencyExcess:
      'Deficiency signs include fatigue, weakness, tingling/numbness in extremities, difficulty walking, mood changes, and memory problems. B12 has no established upper tolerable limit as excess is excreted in urine, though very high doses are unnecessary (ref 3).',
    ageNotes:
      'Adults over 50 are at higher risk of deficiency due to reduced stomach acid (needed for B12 absorption). The NIH recommends older adults obtain B12 from fortified foods or supplements (ref 3).',
    myths: [
      {
        myth: 'You can get enough B12 from a vegan diet without supplementation.',
        fact: 'No reliable unfortified plant food sources of B12 exist. Vegan and vegetarian nutrition authorities uniformly recommend B12 supplementation or fortified foods (ref 4).',
      },
      {
        myth: 'B12 injections give everyone an energy boost.',
        fact: 'B12 injections correct deficiency and resolve associated fatigue, but there is no evidence they boost energy in people with adequate B12 levels (ref 1).',
      },
    ],
    relatedTopics: ['iron', 'metabolism', 'hair-health', 'anti-aging', 'hormones'],
    references: [
      {
        id: 1,
        text: 'Stabler SP. Vitamin B12 deficiency. N Engl J Med. 2013;368(2):149-160.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/23301732/',
      },
      {
        id: 2,
        text: 'Green R, et al. Vitamin B12 deficiency. Nat Rev Dis Primers. 2017;3:17040.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/28660890/',
      },
      {
        id: 3,
        text: 'NIH Office of Dietary Supplements. Vitamin B12 Fact Sheet for Health Professionals.',
        url: 'https://ods.od.nih.gov/factsheets/VitaminB12-HealthProfessional/',
      },
      {
        id: 4,
        text: 'Pawlak R, et al. How prevalent is vitamin B12 deficiency among vegetarians? Nutr Rev. 2013;71(2):110-117.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/23356638/',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 10. Iron
  // ─────────────────────────────────────────────
  {
    slug: 'iron',
    title: 'Iron',
    synonyms: ['ferritin', 'haem iron', 'heme iron', 'iron deficiency', 'anaemia'],
    whatItIs:
      'Iron is an essential mineral that the body uses to make haemoglobin (carries oxygen in red blood cells) and myoglobin (carries oxygen in muscles). It exists in two dietary forms: haem iron (animal sources, better absorbed) and non-haem iron (plant sources).',
    whyItMatters:
      'Iron deficiency is the most common nutritional deficiency worldwide, affecting an estimated 1.6 billion people. It causes anaemia, fatigue, impaired cognitive function, and weakened immunity. Adequate iron is critical for oxygen transport, energy metabolism, and DNA synthesis (ref 1, 2).',
    sources: [
      'Red meat (beef, lamb)',
      'Liver',
      'Oysters',
      'Spinach',
      'Lentils',
      'Chickpeas',
      'Tofu',
      'Fortified cereals',
      'Pumpkin seeds',
      'Dark chocolate (70%+ cocoa)',
    ],
    howMuch:
      'The RDA is 8 mg/day for adult men and post-menopausal women, and 18 mg/day for pre-menopausal women. The upper tolerable limit is 45 mg/day. Vegetarians may need 1.8 times the RDA due to lower bioavailability of non-haem iron (ref 3).',
    deficiencyExcess:
      'Deficiency causes fatigue, pallor, shortness of breath, brittle nails, cold extremities, and pica (craving non-food items). Excess iron (haemochromatosis or over-supplementation) can cause liver damage, heart problems, and joint pain (ref 3).',
    ageNotes:
      'Pre-menopausal women and pregnant women have the highest requirements. Older adults should have ferritin levels checked, as both deficiency and excess are common in this group.',
    myths: [
      {
        myth: 'Spinach is the best source of iron.',
        fact: 'While spinach contains iron, it also has high oxalate content that inhibits absorption. Haem iron from animal sources is 2-3 times better absorbed. Pairing non-haem iron with vitamin C significantly improves absorption (ref 4).',
      },
      {
        myth: 'Everyone should take iron supplements.',
        fact: 'Iron supplements should only be taken when deficiency is confirmed by blood tests. Unnecessary supplementation can cause iron overload, which is associated with oxidative damage and organ toxicity (ref 3).',
      },
    ],
    relatedTopics: ['vitamin-c', 'vitamin-b12', 'hair-health', 'metabolism', 'strength-training'],
    references: [
      {
        id: 1,
        text: 'WHO. Iron deficiency anaemia: assessment, prevention and control. Geneva: World Health Organization, 2001.',
        url: 'https://www.who.int/publications/m/item/iron-children-6to23--archived-iron-deficiency-anaemia-assessment-prevention-and-control',
      },
      {
        id: 2,
        text: 'Camaschella C. Iron-deficiency anemia. N Engl J Med. 2015;372(19):1832-1843.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/25946282/',
      },
      {
        id: 3,
        text: 'NIH Office of Dietary Supplements. Iron Fact Sheet for Health Professionals.',
        url: 'https://ods.od.nih.gov/factsheets/Iron-HealthProfessional/',
      },
      {
        id: 4,
        text: 'Hallberg L, Brune M, Rossander L. Iron absorption in man: ascorbic acid and dose-dependent inhibition by phytate. Am J Clin Nutr. 1989;49(1):140-144.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/2911999/',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 11. Magnesium
  // ─────────────────────────────────────────────
  {
    slug: 'magnesium',
    title: 'Magnesium',
    synonyms: ['mag', 'magnesium glycinate', 'magnesium citrate'],
    whatItIs:
      'Magnesium is an essential mineral involved in over 300 enzymatic reactions in the body, including energy production, protein synthesis, muscle and nerve function, and blood pressure regulation.',
    whyItMatters:
      'Subclinical magnesium deficiency is estimated to affect up to 50% of the US population [VERIFY]. Inadequate magnesium intake is associated with increased risk of cardiovascular disease, type 2 diabetes, osteoporosis, and migraines. It plays a critical role in sleep quality, muscle relaxation, and stress response (ref 1, 2).',
    sources: [
      'Pumpkin seeds',
      'Almonds',
      'Spinach',
      'Dark chocolate (70%+ cocoa)',
      'Black beans',
      'Avocado',
      'Cashews',
      'Brown rice',
      'Bananas',
      'Edamame',
    ],
    howMuch:
      'The RDA is 400-420 mg/day for adult men and 310-320 mg/day for adult women (ref 3). The upper tolerable limit for supplemental magnesium is 350 mg/day (this does not include dietary intake). Requirements increase during pregnancy.',
    deficiencyExcess:
      'Deficiency signs include muscle cramps, tremors, fatigue, insomnia, irritability, and abnormal heart rhythms. Excess supplemental magnesium can cause diarrhoea, nausea, and abdominal cramping (the osmotic laxative effect).',
    ageNotes:
      'Older adults absorb less magnesium and are more likely to take medications that deplete it (e.g., proton pump inhibitors, diuretics). Magnesium glycinate or citrate forms are generally better tolerated and absorbed.',
    myths: [
      {
        myth: 'Magnesium supplements are all the same.',
        fact: 'Different forms have different bioavailability and effects. Magnesium citrate and glycinate are well-absorbed; magnesium oxide is poorly absorbed but effective as a laxative (ref 4).',
      },
      {
        myth: 'You can get all the magnesium you need from diet alone.',
        fact: 'While possible, modern agricultural practices have reduced soil magnesium content, and processed foods are low in magnesium. Many people may benefit from supplementation (ref 1).',
      },
    ],
    relatedTopics: [
      'sleep',
      'bone-density',
      'muscle-recovery',
      'strength-training',
      'hormones',
      'calcium',
    ],
    references: [
      {
        id: 1,
        text: 'DiNicolantonio JJ, O\'Keefe JH, Wilson W. Subclinical magnesium deficiency: a principal driver of cardiovascular disease and a public health crisis. Open Heart. 2018;5(1):e000668.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/29387426/',
      },
      {
        id: 2,
        text: 'Barbagallo M, Dominguez LJ. Magnesium and type 2 diabetes. World J Diabetes. 2015;6(10):1152-1157.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/26322160/',
      },
      {
        id: 3,
        text: 'NIH Office of Dietary Supplements. Magnesium Fact Sheet for Health Professionals.',
        url: 'https://ods.od.nih.gov/factsheets/Magnesium-HealthProfessional/',
      },
      {
        id: 4,
        text: 'Firoz M, Graber M. Bioavailability of US commercial magnesium preparations. Magnes Res. 2001;14(4):257-262.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/11794633/',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 12. Calcium
  // ─────────────────────────────────────────────
  {
    slug: 'calcium',
    title: 'Calcium',
    synonyms: ['calcium carbonate', 'calcium citrate', 'dairy calcium'],
    whatItIs:
      'Calcium is the most abundant mineral in the body, with 99% stored in bones and teeth. It is essential for skeletal structure, muscle contraction, nerve signalling, and blood clotting.',
    whyItMatters:
      'Adequate calcium intake throughout life is critical for building and maintaining bone density, reducing osteoporosis risk. Calcium also plays roles in muscle function, blood vessel contraction and dilation, and hormone secretion (ref 1, 2).',
    sources: [
      'Dairy (milk, yoghurt, cheese)',
      'Sardines (with bones)',
      'Fortified plant milks',
      'Tofu (calcium-set)',
      'Kale',
      'Broccoli',
      'Bok choy',
      'Almonds',
      'Fortified orange juice',
      'Sesame seeds (tahini)',
    ],
    howMuch:
      'The RDA is 1,000 mg/day for adults 19-50 and men 51-70; 1,200 mg/day for women 51+ and adults 71+ (ref 3). The upper tolerable limit is 2,500 mg/day for adults 19-50 and 2,000 mg/day for 51+. Vitamin D is required for calcium absorption.',
    deficiencyExcess:
      'Chronic inadequacy leads to reduced bone density and increased fracture risk. Excess calcium (especially from supplements) has been associated with increased risk of kidney stones and, controversially, cardiovascular calcification (ref 4).',
    ageNotes:
      'Post-menopausal women and older adults have higher requirements due to declining oestrogen (which supports bone retention) and reduced absorption efficiency. Vitamin D co-supplementation is typically recommended.',
    myths: [
      {
        myth: 'Dairy is the only way to get enough calcium.',
        fact: 'While dairy is calcium-rich, many non-dairy sources (fortified plant milks, tofu, leafy greens, sardines) can meet requirements. Some populations with low dairy intake have adequate bone health (ref 1).',
      },
      {
        myth: 'More calcium always means stronger bones.',
        fact: 'Beyond adequate intake, additional calcium does not linearly strengthen bones. Weight-bearing exercise, vitamin D, vitamin K, and overall diet pattern also play critical roles (ref 2).',
      },
    ],
    relatedTopics: ['vitamin-k', 'bone-density', 'magnesium', 'vitamin-a', 'strength-training'],
    references: [
      {
        id: 1,
        text: 'Weaver CM, et al. Calcium plus vitamin D supplementation and risk of fractures: an updated meta-analysis from the National Osteoporosis Foundation. Osteoporos Int. 2016;27(1):367-376.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/26510847/',
      },
      {
        id: 2,
        text: 'Tai V, et al. Calcium intake and bone mineral density: systematic review and meta-analysis. BMJ. 2015;351:h4183.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/26420598/',
      },
      {
        id: 3,
        text: 'NIH Office of Dietary Supplements. Calcium Fact Sheet for Health Professionals.',
        url: 'https://ods.od.nih.gov/factsheets/Calcium-HealthProfessional/',
      },
      {
        id: 4,
        text: 'Bolland MJ, et al. Effect of calcium supplements on risk of myocardial infarction and cardiovascular events: meta-analysis. BMJ. 2010;341:c3691.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/20671013/',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 13. Hair Health
  // ─────────────────────────────────────────────
  {
    slug: 'hair-health',
    title: 'Hair Health',
    synonyms: ['hair loss', 'hair growth', 'alopecia', 'thinning hair'],
    whatItIs:
      'Hair health encompasses the growth, strength, and appearance of hair, which is influenced by genetics, nutrition, hormones, and overall health. Each hair follicle cycles through growth (anagen), regression (catagen), and rest (telogen) phases.',
    whyItMatters:
      'Hair loss and thinning affect a significant portion of the population and can indicate underlying nutritional deficiencies or hormonal imbalances. Nutritional factors -- particularly iron, biotin, zinc, vitamin D, and protein -- play a well-documented role in hair health (ref 1, 2).',
    sources: [
      'Eggs (biotin, protein)',
      'Salmon (omega-3, protein)',
      'Spinach (iron, folate)',
      'Sweet potatoes (beta-carotene)',
      'Nuts and seeds (zinc, selenium)',
      'Oysters (zinc)',
      'Avocados (vitamin E, healthy fats)',
      'Berries (vitamin C, antioxidants)',
      'Lean meats (protein, iron)',
      'Legumes (protein, iron, biotin)',
    ],
    howMuch:
      'There is no single RDA for "hair health." Focus on meeting RDAs for iron (see iron topic), zinc (8-11 mg/day), biotin (30 mcg/day AI), and ensuring adequate protein intake (ref 3). Blood tests for ferritin, vitamin D, thyroid function, and zinc can help identify deficiencies.',
    deficiencyExcess:
      'Iron deficiency (ferritin <30 ng/mL) is strongly associated with hair loss, particularly in women. Zinc deficiency, severe protein restriction, and vitamin D insufficiency are also linked to hair shedding. Excess vitamin A (>10,000 IU/day) can paradoxically cause hair loss (ref 1, 2).',
    ageNotes:
      'Androgenetic alopecia (pattern hair loss) affects most men and many women with age. Nutritional optimisation cannot override genetic hair loss but can prevent or reverse nutritional causes of shedding.',
    myths: [
      {
        myth: 'Biotin supplements will regrow lost hair.',
        fact: 'Biotin supplementation only helps when there is a true biotin deficiency, which is rare. Most hair supplement marketing lacks rigorous clinical evidence (ref 4).',
      },
      {
        myth: 'Cutting hair makes it grow thicker.',
        fact: 'Hair thickness is determined at the follicle level. Cutting has no effect on hair growth rate or thickness -- it only affects the appearance of the hair ends.',
      },
    ],
    relatedTopics: [
      'iron',
      'protein',
      'vitamin-a',
      'omega-3',
      'vitamin-b12',
      'hormones',
      'skin-health',
    ],
    references: [
      {
        id: 1,
        text: 'Almohanna HM, et al. The Role of Vitamins and Minerals in Hair Loss: A Review. Dermatol Ther (Heidelb). 2019;9(1):51-70.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/30547302/',
      },
      {
        id: 2,
        text: 'Guo EL, Katta R. Diet and hair loss: effects of nutrient deficiency and supplement use. Dermatol Pract Concept. 2017;7(1):1-10.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/28243487/',
      },
      {
        id: 3,
        text: 'NIH Office of Dietary Supplements. Biotin Fact Sheet for Health Professionals.',
        url: 'https://ods.od.nih.gov/factsheets/Biotin-HealthProfessional/',
      },
      {
        id: 4,
        text: 'Patel DP, Swink SM, Castelo-Soccio L. A Review of the Use of Biotin for Hair Loss. Skin Appendage Disord. 2017;3(3):166-169.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/28879195/',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 14. Skin Health
  // ─────────────────────────────────────────────
  {
    slug: 'skin-health',
    title: 'Skin Health',
    synonyms: ['skin care', 'skin aging', 'collagen', 'glycation', 'skin nutrition'],
    whatItIs:
      'Skin health refers to the structural integrity, hydration, elasticity, and appearance of the skin, the body\'s largest organ. Nutrition, hydration, sun protection, and lifestyle factors all play significant roles.',
    whyItMatters:
      'The skin serves as a barrier against pathogens, UV radiation, and environmental damage. Age-related changes (wrinkles, sagging, hyperpigmentation) are accelerated by UV exposure, smoking, poor nutrition, and glycation (sugar-protein cross-linking). Evidence suggests that dietary patterns rich in antioxidants, healthy fats, and collagen-supporting nutrients are associated with healthier skin aging (ref 1, 2).',
    sources: [
      'Fatty fish (omega-3)',
      'Avocados (vitamin E, healthy fats)',
      'Sweet potatoes (beta-carotene)',
      'Red bell peppers (vitamin C)',
      'Broccoli (vitamins A, C, zinc)',
      'Tomatoes (lycopene)',
      'Walnuts (omega-3, zinc)',
      'Bone broth (collagen, glycine)',
      'Green tea (EGCG polyphenols)',
      'Dark chocolate (flavanols)',
    ],
    howMuch:
      'No single RDA for skin health exists. Key nutrients: vitamin C (75-90 mg/day), vitamin E (15 mg/day), zinc (8-11 mg/day), omega-3 (250-500 mg EPA+DHA/day). Hydrolysed collagen supplements at 2.5-15 g/day have shown modest benefits in some trials (ref 3) [VERIFY specific dosing].',
    deficiencyExcess:
      'Vitamin C deficiency impairs collagen synthesis (scurvy). Zinc deficiency causes dermatitis and poor wound healing. Essential fatty acid deficiency leads to dry, scaly skin. Excess vitamin A (retinol) can cause skin peeling and irritation.',
    ageNotes:
      'Collagen production declines approximately 1% per year after age 20 [VERIFY]. Sun protection remains the single most effective anti-aging intervention for skin at any age. Glycation from high sugar intake accelerates visible skin aging (ref 4).',
    myths: [
      {
        myth: 'Expensive skin creams can reverse deep wrinkles.',
        fact: 'Topical products can modestly improve skin hydration and fine lines, but deep wrinkles are structural changes that topicals cannot fully reverse. Nutrition, sun protection, and retinoids have the strongest evidence base (ref 1).',
      },
      {
        myth: 'Drinking more water automatically improves skin.',
        fact: 'Severe dehydration affects skin turgor, but there is limited evidence that drinking water above adequate hydration levels significantly improves skin appearance in well-hydrated individuals (ref 5).',
      },
    ],
    relatedTopics: [
      'vitamin-c',
      'vitamin-a',
      'omega-3',
      'good-fats',
      'hydration',
      'anti-aging',
      'sugar',
      'inflammation',
    ],
    references: [
      {
        id: 1,
        text: 'Pullar JM, Carr AC, Vissers MCM. The Roles of Vitamin C in Skin Health. Nutrients. 2017;9(8):866.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/28805671/',
      },
      {
        id: 2,
        text: 'Schagen SK, et al. Discovering the link between nutrition and skin aging. Dermatoendocrinol. 2012;4(3):298-307.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/23467449/',
      },
      {
        id: 3,
        text: 'Choi FD, et al. Oral Collagen Supplementation: A Systematic Review of Dermatological Applications. J Drugs Dermatol. 2019;18(1):9-16.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/30681787/',
      },
      {
        id: 4,
        text: 'Danby FW. Nutrition and aging skin: sugar and glycation. Clin Dermatol. 2010;28(4):409-411.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/20620757/',
      },
      {
        id: 5,
        text: 'Palma L, et al. Dietary water affects human skin hydration and biomechanics. Clin Cosmet Investig Dermatol. 2015;8:413-421.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/26345226/',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 15. Belly Fat
  // ─────────────────────────────────────────────
  {
    slug: 'belly-fat',
    title: 'Belly Fat',
    synonyms: ['visceral fat', 'abdominal fat', 'central obesity', 'abdominal obesity'],
    whatItIs:
      'Belly fat encompasses both subcutaneous fat (under the skin) and visceral fat (around the organs) in the abdominal region. Visceral fat is the metabolically dangerous component.',
    whyItMatters:
      'Excess abdominal fat, particularly visceral fat, is one of the strongest predictors of metabolic disease. It is associated with insulin resistance, type 2 diabetes, cardiovascular disease, and systemic inflammation. Waist circumference is considered a better predictor of cardiometabolic risk than BMI alone (ref 1, 2).',
    sources: [
      'Vegetables (high fibre, low calorie)',
      'Lean proteins (chicken, fish, tofu)',
      'Whole grains (oats, quinoa, brown rice)',
      'Legumes (beans, lentils)',
      'Nuts (in moderate portions)',
      'Berries',
      'Green tea',
      'Olive oil',
      'Fatty fish (omega-3)',
      'Apple cider vinegar (modest evidence)',
    ],
    howMuch:
      'Waist circumference guidelines: men should aim for <94 cm (37 in), women <80 cm (31.5 in) per WHO standards. Substantially increased risk: men >102 cm (40 in), women >88 cm (35 in) (ref 3). Fat loss requires a caloric deficit; targeted "spot reduction" is not supported by evidence.',
    deficiencyExcess:
      'Some body fat is essential for hormone production and organ protection. Excessively low body fat can disrupt hormones (amenorrhoea in women, low testosterone in men) and impair immune function. The goal is reducing excess visceral fat, not eliminating all abdominal fat.',
    ageNotes:
      'Visceral fat tends to increase with age due to declining hormones (oestrogen, testosterone) and reduced physical activity. Resistance training is particularly effective at combating age-related visceral fat accumulation (ref 4).',
    myths: [
      {
        myth: 'Crunches and sit-ups burn belly fat.',
        fact: 'Spot reduction of fat is not supported by evidence. Abdominal exercises strengthen core muscles but do not preferentially burn belly fat. Overall caloric deficit and exercise reduce total body fat, including visceral fat (ref 5).',
      },
      {
        myth: 'Certain foods "burn" belly fat.',
        fact: 'No single food directly burns belly fat. However, diets rich in fibre, protein, and whole foods are associated with lower visceral fat levels, likely through improved satiety and metabolic effects (ref 1).',
      },
    ],
    relatedTopics: [
      'visceral-fat',
      'inflammation',
      'metabolism',
      'sugar',
      'strength-training',
      'hormones',
      'gut-health',
    ],
    references: [
      {
        id: 1,
        text: 'Tchernof A, Despres JP. Pathophysiology of human visceral obesity: an update. Physiol Rev. 2013;93(1):359-404.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/23303913/',
      },
      {
        id: 2,
        text: 'Ross R, et al. Waist circumference as a vital sign in clinical practice: a Consensus Statement from the IAS and ICCR Working Group on Visceral Obesity. Nat Rev Endocrinol. 2020;16(3):177-189.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/32020062/',
      },
      {
        id: 3,
        text: 'WHO. Waist Circumference and Waist-Hip Ratio: Report of a WHO Expert Consultation. Geneva, 2008.',
        url: 'https://www.who.int/publications/i/item/9789241501491',
      },
      {
        id: 4,
        text: 'Ismail I, et al. A systematic review and meta-analysis of the effect of aerobic vs. resistance exercise training on visceral fat. Obes Rev. 2012;13(1):68-91.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/21951360/',
      },
      {
        id: 5,
        text: 'Vispute SS, et al. The effect of abdominal exercise on abdominal fat. J Strength Cond Res. 2011;25(9):2559-2564.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/21804427/',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 16. Visceral Fat
  // ─────────────────────────────────────────────
  {
    slug: 'visceral-fat',
    title: 'Visceral Fat',
    synonyms: ['organ fat', 'deep fat', 'intra-abdominal fat', 'VAT'],
    whatItIs:
      'Visceral fat is adipose tissue stored deep within the abdominal cavity, surrounding vital organs like the liver, pancreas, and intestines. Unlike subcutaneous fat (under the skin), visceral fat is metabolically active and secretes inflammatory cytokines.',
    whyItMatters:
      'Visceral fat acts as an endocrine organ, releasing pro-inflammatory molecules (IL-6, TNF-alpha) and hormones that promote insulin resistance, dyslipidaemia, and hypertension. It is independently associated with increased risk of type 2 diabetes, cardiovascular disease, certain cancers, and all-cause mortality, even in people with normal BMI (ref 1, 2).',
    sources: [
      'Mediterranean-style diet foods',
      'High-fibre vegetables',
      'Lean proteins',
      'Whole grains',
      'Fatty fish',
      'Nuts and seeds',
      'Legumes',
      'Green tea',
      'Foods low in added sugar',
      'Olive oil',
    ],
    howMuch:
      'Visceral fat can be estimated via waist circumference (see belly-fat topic) or measured precisely by DEXA scan or MRI. On consumer body composition scales, a visceral fat rating of 1-12 is generally considered healthy; 13+ indicates excess [VERIFY scale-specific ranges]. Even modest weight loss (5-10% of body weight) significantly reduces visceral fat (ref 3).',
    deficiencyExcess:
      'Everyone has some visceral fat, which is normal and protective in small amounts. Excess visceral fat is the primary driver of metabolic syndrome. Key indicators include elevated waist circumference, high triglycerides, low HDL, elevated fasting glucose, and high blood pressure.',
    ageNotes:
      'Visceral fat accumulates more readily with age, especially after menopause in women and with declining testosterone in men. Regular physical activity, particularly resistance training and HIIT, is the most effective intervention.',
    myths: [
      {
        myth: 'Thin people cannot have dangerous visceral fat.',
        fact: '"TOFI" (thin outside, fat inside) is a well-documented phenomenon where normal-weight individuals carry excess visceral fat and have elevated metabolic risk (ref 4).',
      },
      {
        myth: 'Liposuction removes visceral fat.',
        fact: 'Liposuction only removes subcutaneous fat. Visceral fat can only be reduced through caloric deficit, exercise, and lifestyle changes (ref 1).',
      },
    ],
    relatedTopics: [
      'belly-fat',
      'inflammation',
      'metabolism',
      'cholesterol',
      'hormones',
      'strength-training',
      'sugar',
    ],
    references: [
      {
        id: 1,
        text: 'Tchernof A, Despres JP. Pathophysiology of human visceral obesity: an update. Physiol Rev. 2013;93(1):359-404.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/23303913/',
      },
      {
        id: 2,
        text: 'Neeland IJ, et al. Visceral and ectopic fat, atherosclerosis, and cardiometabolic disease: a position statement. Lancet Diabetes Endocrinol. 2019;7(9):715-725.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/31301983/',
      },
      {
        id: 3,
        text: 'Verheggen RJ, et al. A systematic review and meta-analysis on the effects of exercise training versus hypocaloric diet: distinct effects on body weight and visceral adipose tissue. Obes Rev. 2016;17(8):664-690.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/27213481/',
      },
      {
        id: 4,
        text: 'Thomas EL, et al. The missing risk: MRI and MRS phenotyping of abdominal adiposity and ectopic fat. Obesity (Silver Spring). 2012;20(1):76-87.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/21660078/',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 17. Back Strength
  // ─────────────────────────────────────────────
  {
    slug: 'back-strength',
    title: 'Back Strength',
    synonyms: ['lower back', 'spinal health', 'posterior chain', 'back pain', 'back exercises'],
    whatItIs:
      'Back strength refers to the muscular fitness and endurance of the muscles supporting the spine, including the erector spinae, multifidus, latissimus dorsi, and the broader posterior chain (glutes, hamstrings). A strong back is foundational for posture, movement, and injury prevention.',
    whyItMatters:
      'Low back pain is the leading cause of disability worldwide, affecting approximately 80% of adults at some point in their lives (ref 1). Strengthening the back and posterior chain is one of the most evidence-based interventions for both preventing and treating non-specific low back pain. A strong back supports daily functional movements and athletic performance (ref 2, 3).',
    sources: [
      'Protein-rich foods for muscle repair',
      'Anti-inflammatory foods (fatty fish, vegetables)',
      'Magnesium-rich foods (for muscle function)',
      'Calcium and vitamin D (for bone support)',
      'Collagen-supporting nutrients (vitamin C, glycine)',
    ],
    howMuch:
      'Current guidelines recommend resistance training 2-3 times per week, including exercises targeting the posterior chain. Key exercises include deadlifts, rows, hip hinges, bird-dogs, and glute bridges. Start with bodyweight and progress gradually (ref 3). Consult a qualified professional for form guidance.',
    deficiencyExcess:
      'Weak back muscles contribute to poor posture, increased injury risk, and chronic pain. Overtraining without adequate recovery can cause muscle strain, disc issues, or facet joint irritation. Balance posterior chain work with core/anterior chain training.',
    ageNotes:
      'Age-related muscle loss (sarcopenia) disproportionately affects the back. Older adults benefit greatly from progressive resistance training but should start conservatively and prioritise form over load.',
    myths: [
      {
        myth: 'Deadlifts are bad for your back.',
        fact: 'When performed with proper form and appropriate load, deadlifts are one of the most effective exercises for back strength. Poor form with excessive load is what causes injury, not the exercise itself (ref 3).',
      },
      {
        myth: 'Bed rest is best for back pain.',
        fact: 'Current evidence strongly recommends staying active over bed rest for most types of back pain. Prolonged inactivity worsens outcomes (ref 2).',
      },
    ],
    relatedTopics: [
      'strength-training',
      'glutes',
      'muscle-recovery',
      'bone-density',
      'inflammation',
      'protein',
    ],
    references: [
      {
        id: 1,
        text: 'Hoy D, et al. The global burden of low back pain: estimates from the Global Burden of Disease 2010 study. Ann Rheum Dis. 2014;73(6):968-974.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/24665116/',
      },
      {
        id: 2,
        text: 'Qaseem A, et al. Noninvasive Treatments for Acute, Subacute, and Chronic Low Back Pain: A Clinical Practice Guideline From the American College of Physicians. Ann Intern Med. 2017;166(7):514-530.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/28192789/',
      },
      {
        id: 3,
        text: 'Tataryn N, et al. Posterior-Chain Resistance Training Compared to General Exercise and Walking Programmes for the Treatment of Chronic Low Back Pain in the General Population: A Systematic Review and Meta-Analysis. Sports Med Open. 2021;7(1):17.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/33624215/',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 18. Anti-Aging
  // ─────────────────────────────────────────────
  {
    slug: 'anti-aging',
    title: 'Anti-Aging & Longevity',
    synonyms: ['longevity', 'aging', 'lifespan', 'healthspan', 'senescence'],
    whatItIs:
      'Anti-aging in the nutritional and lifestyle context refers to evidence-based strategies to slow biological aging, extend healthspan (years of healthy living), and reduce the risk of age-related diseases.',
    whyItMatters:
      'Aging is driven by interconnected processes: oxidative stress, chronic inflammation ("inflammaging"), telomere shortening, mitochondrial dysfunction, and cellular senescence. Lifestyle factors -- diet, exercise, sleep, and stress management -- significantly influence the rate of biological aging and are modifiable (ref 1, 2).',
    sources: [
      'Blueberries (anthocyanins)',
      'Fatty fish (omega-3)',
      'Leafy greens (folate, vitamin K)',
      'Nuts (vitamin E, polyphenols)',
      'Olive oil (oleocanthal, MUFAs)',
      'Green tea (EGCG)',
      'Turmeric (curcumin)',
      'Tomatoes (lycopene)',
      'Dark chocolate (flavanols)',
      'Cruciferous vegetables (sulforaphane)',
    ],
    howMuch:
      'No single "anti-aging dose" exists. The Mediterranean diet pattern has the strongest evidence base for longevity (ref 3). Key principles: emphasise whole foods, limit added sugars and ultra-processed foods, maintain adequate protein for muscle preservation, and ensure sufficient micronutrient intake.',
    deficiencyExcess:
      'Caloric restriction extends lifespan in animal models, but extreme restriction in humans risks muscle loss, bone density decline, and immune suppression. The goal is nutrient density, not deprivation. Over-supplementation with antioxidants may paradoxically increase harm (ref 4).',
    ageNotes:
      'It is never too late to benefit from lifestyle changes. Evidence suggests that adopting healthy habits even in midlife or later significantly reduces risk of age-related disease and disability.',
    myths: [
      {
        myth: 'Anti-aging supplements can reverse aging.',
        fact: 'No supplement has been proven to reverse aging in humans. The most robust evidence supports lifestyle factors: diet quality, regular exercise, adequate sleep, stress management, and not smoking (ref 1).',
      },
      {
        myth: 'Aging is purely genetic.',
        fact: 'Twin studies suggest genetics account for only about 20-30% of lifespan variation. Environment and lifestyle are the dominant factors (ref 5).',
      },
    ],
    relatedTopics: [
      'inflammation',
      'omega-3',
      'sleep',
      'strength-training',
      'skin-health',
      'metabolism',
      'vitamin-c',
      'gut-health',
    ],
    references: [
      {
        id: 1,
        text: 'Lopez-Otin C, et al. The hallmarks of aging. Cell. 2013;153(6):1194-1217.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/23746838/',
      },
      {
        id: 2,
        text: 'Franceschi C, et al. Inflammaging: a new immune-metabolic viewpoint for age-related diseases. Nat Rev Endocrinol. 2018;14(10):576-590.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/30046148/',
      },
      {
        id: 3,
        text: 'Sofi F, et al. Accruing evidence on benefits of adherence to the Mediterranean diet on health: an updated systematic review and meta-analysis. Am J Clin Nutr. 2010;92(5):1189-1196.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/20810976/',
      },
      {
        id: 4,
        text: 'Bjelakovic G, et al. Antioxidant supplements for prevention of mortality in healthy participants and patients with various diseases. Cochrane Database Syst Rev. 2012;(3):CD007176.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/22419320/',
      },
      {
        id: 5,
        text: 'Herskind AM, et al. The heritability of human longevity: a population-based study of 2872 Danish twin pairs born 1870-1900. Hum Genet. 1996;97(3):319-323.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/8786073/',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 19. Strength Training
  // ─────────────────────────────────────────────
  {
    slug: 'strength-training',
    title: 'Strength Training',
    synonyms: [
      'resistance training',
      'weight training',
      'lifting',
      'weightlifting',
      'progressive overload',
    ],
    whatItIs:
      'Strength training is a form of exercise that uses resistance (bodyweight, free weights, machines, or bands) to induce muscular contraction, building strength, endurance, and muscle size over time through progressive overload.',
    whyItMatters:
      'Strength training is associated with reduced all-cause mortality, improved metabolic health (insulin sensitivity, body composition), increased bone density, better functional capacity, and reduced risk of falls in older adults. Current evidence suggests it may be as important as cardiovascular exercise for long-term health (ref 1, 2, 3).',
    sources: [
      'High-quality protein (chicken, fish, eggs, legumes)',
      'Complex carbohydrates (for training fuel)',
      'Creatine monohydrate (evidence-based supplement)',
      'Adequate calories for recovery',
      'Magnesium and zinc (muscle function)',
      'Tart cherry juice (recovery, modest evidence)',
    ],
    howMuch:
      'Current guidelines recommend resistance training at least 2 days per week, targeting all major muscle groups (ref 4). Evidence suggests 10-20 sets per muscle group per week is a reasonable range for most people. Progressive overload (gradually increasing weight, reps, or volume) is the key driver of adaptation.',
    deficiencyExcess:
      'Insufficient resistance training contributes to sarcopenia, reduced bone density, and metabolic decline. Overtraining without adequate recovery leads to elevated cortisol, impaired immune function, injury, and diminished performance. Signs include persistent fatigue, declining performance, and mood disturbances.',
    ageNotes:
      'Strength training becomes increasingly important with age. Adults over 65 who resistance train have significantly lower risk of falls, fractures, and functional disability. It is safe and effective at any age with appropriate programming (ref 2).',
    myths: [
      {
        myth: 'Lifting weights makes women bulky.',
        fact: 'Women have significantly lower testosterone levels than men, making it very difficult to gain large amounts of muscle mass. Resistance training in women typically produces a leaner, more toned physique (ref 1).',
      },
      {
        myth: 'Muscle turns into fat when you stop training.',
        fact: 'Muscle and fat are completely different tissues. When training stops, muscle atrophies and caloric surplus may increase fat storage, but one tissue does not transform into another.',
      },
      {
        myth: 'You need to be sore to have had an effective workout.',
        fact: 'Delayed onset muscle soreness (DOMS) is not a reliable indicator of workout quality. Progressive overload and consistent training are what drive adaptation (ref 5).',
      },
    ],
    relatedTopics: [
      'protein',
      'protein-timing',
      'muscle-recovery',
      'bone-density',
      'back-strength',
      'glutes',
      'metabolism',
      'hormones',
    ],
    references: [
      {
        id: 1,
        text: 'Westcott WL. Resistance training is medicine: effects of strength training on health. Curr Sports Med Rep. 2012;11(4):209-216.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/22777332/',
      },
      {
        id: 2,
        text: 'Liu CJ, Latham NK. Progressive resistance strength training for improving physical function in older adults. Cochrane Database Syst Rev. 2009;(3):CD002759.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/19588334/',
      },
      {
        id: 3,
        text: 'Momma H, et al. Muscle-strengthening activities are associated with lower risk and mortality in major non-communicable diseases: a systematic review and meta-analysis of cohort studies. Br J Sports Med. 2022;56(13):755-763.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/35228201/',
      },
      {
        id: 4,
        text: 'US Department of Health and Human Services. Physical Activity Guidelines for Americans, 2nd edition. 2018.',
        url: 'https://health.gov/our-work/nutrition-physical-activity/physical-activity-guidelines/current-guidelines',
      },
      {
        id: 5,
        text: 'Schoenfeld BJ, Contreras B. Is Postexercise Muscle Soreness a Valid Indicator of Muscular Adaptations? Strength Cond J. 2013;35(5):16-21.',
        url: 'https://journals.lww.com/nsca-scj/fulltext/2013/10000/is_postexercise_muscle_soreness_a_valid_indicator.3.aspx',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 20. Glutes
  // ─────────────────────────────────────────────
  {
    slug: 'glutes',
    title: 'Glutes',
    synonyms: ['gluteal muscles', 'hip strength', 'butt', 'glute training', 'hip extension'],
    whatItIs:
      'The glutes are a group of three muscles (gluteus maximus, medius, and minimus) forming the buttocks. They are among the largest and most powerful muscles in the body, responsible for hip extension, abduction, and external rotation.',
    whyItMatters:
      'Strong glutes are essential for posture, gait, athletic performance, and injury prevention. Gluteal weakness or "amnesia" (from prolonged sitting) is associated with lower back pain, knee pain, hip dysfunction, and poor movement mechanics. The gluteus maximus is the primary driver of hip extension, critical for walking, running, climbing, and standing from sitting (ref 1, 2).',
    sources: [
      'Adequate protein for muscle growth',
      'Complex carbohydrates (training fuel)',
      'Creatine monohydrate',
      'Anti-inflammatory foods for recovery',
      'Adequate total calories to support hypertrophy',
    ],
    howMuch:
      'For glute development, evidence suggests training 2-4 times per week with a mix of compound movements (squats, deadlifts, hip thrusts) and isolation exercises (hip abduction, glute bridges). A combination of heavy loading and moderate-rep work with a focus on mind-muscle connection appears optimal (ref 3). Progressive overload is essential.',
    deficiencyExcess:
      'Weak glutes (gluteal inhibition) are associated with anterior pelvic tilt, knee valgus, IT band syndrome, and low back pain. Overtraining the glutes without balancing other muscle groups can create imbalances.',
    ageNotes:
      'Gluteal strength declines with age and sedentary behaviour. Strong glutes are protective against falls and hip fractures in older adults. Hip thrust and glute bridge variations can be performed safely at any age.',
    myths: [
      {
        myth: 'Squats alone are enough for glute development.',
        fact: 'While squats activate the glutes, EMG studies show that hip thrusts produce higher gluteal activation, particularly at full hip extension. A varied programme is optimal (ref 3).',
      },
      {
        myth: 'You need to train glutes every day for results.',
        fact: 'Muscles grow during recovery, not during training. Evidence supports 2-4 sessions per week with adequate rest between sessions for optimal hypertrophy (ref 4).',
      },
    ],
    relatedTopics: [
      'strength-training',
      'back-strength',
      'muscle-recovery',
      'protein',
      'bone-density',
    ],
    references: [
      {
        id: 1,
        text: 'Distefano LJ, et al. Gluteal muscle activation during common therapeutic exercises. J Orthop Sports Phys Ther. 2009;39(7):532-540.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/19574661/',
      },
      {
        id: 2,
        text: 'Jeong UC, et al. The effects of gluteus muscle strengthening exercise and lumbar stabilization exercise on lumbar muscle strength and balance in chronic low back pain patients. J Phys Ther Sci. 2015;27(12):3813-3816.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/26834359/',
      },
      {
        id: 3,
        text: 'Contreras B, et al. A Comparison of Gluteus Maximus, Biceps Femoris, and Vastus Lateralis Electromyography Amplitude in the Parallel, Full, and Front Squat Variations in Resistance-Trained Females. J Appl Biomech. 2016;32(1):16-22.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/26252837/',
      },
      {
        id: 4,
        text: 'Schoenfeld BJ, et al. Effects of Resistance Training Frequency on Measures of Muscle Hypertrophy: A Systematic Review and Meta-Analysis. Sports Med. 2016;46(11):1689-1697.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/27102172/',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 21. Gluten
  // ─────────────────────────────────────────────
  {
    slug: 'gluten',
    title: 'Gluten',
    synonyms: ['celiac', 'coeliac', 'gluten-free', 'wheat protein', 'gliadin'],
    whatItIs:
      'Gluten is a family of storage proteins (primarily gliadin and glutenin) found in wheat, barley, and rye. It provides elasticity to dough and is a common component of many processed foods.',
    whyItMatters:
      'For the approximately 1% of the population with coeliac disease, gluten triggers an autoimmune response that damages the small intestinal lining, causing malabsorption and a range of systemic symptoms. An additional estimated 0.5-13% may have non-coeliac gluten sensitivity (NCGS), though this condition is less well-defined and the subject of ongoing research (ref 1, 2). For the general population without these conditions, gluten-containing whole grains are associated with health benefits.',
    sources: [
      'Gluten-free whole grains: rice, quinoa, oats (certified GF), millet, buckwheat, amaranth',
      'Naturally gluten-free: all fruits, vegetables, meats, fish, eggs, dairy, legumes, nuts, seeds',
      'Gluten-containing: wheat, barley, rye, spelt, kamut, triticale',
    ],
    howMuch:
      'People with coeliac disease must eliminate all gluten (even trace amounts under 20 ppm can trigger damage) (ref 3). Those with NCGS may tolerate varying amounts. For the general healthy population, there is no evidence-based reason to avoid gluten, and gluten-free diets may lack fibre and certain micronutrients if not carefully planned.',
    deficiencyExcess:
      'Coeliac disease causes villous atrophy, leading to deficiencies in iron, calcium, vitamin D, B12, and folate. Unnecessary gluten-free diets can be lower in fibre, B vitamins, and iron if whole grains are simply removed without adequate substitution.',
    ageNotes:
      'Coeliac disease can develop at any age. Diagnosis is often delayed, with average diagnostic delay of 6-10 years [VERIFY]. Testing (tTG-IgA antibody) should be done while still consuming gluten.',
    myths: [
      {
        myth: 'Gluten-free diets are healthier for everyone.',
        fact: 'For people without coeliac disease or NCGS, a gluten-free diet offers no demonstrated health benefit and may be nutritionally inferior. Many gluten-free processed products are higher in sugar and lower in fibre (ref 4).',
      },
      {
        myth: 'Gluten sensitivity is not real.',
        fact: 'While less well-characterised than coeliac disease, NCGS is recognised by gastroenterology societies. Double-blind crossover trials have shown symptomatic responses in a subset of patients, though the mechanism is debated (ref 2).',
      },
    ],
    relatedTopics: ['gut-health', 'inflammation', 'iron', 'fibre', 'vitamin-b12'],
    references: [
      {
        id: 1,
        text: 'Lebwohl B, et al. Coeliac disease. Lancet. 2018;391(10115):70-81.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/28760445/',
      },
      {
        id: 2,
        text: 'Catassi C, et al. Non-Celiac Gluten Sensitivity: The New Frontier of Gluten Related Disorders. Nutrients. 2013;5(10):3839-3853.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/24077239/',
      },
      {
        id: 3,
        text: 'FDA. Gluten-Free Labeling of Foods. Code of Federal Regulations Title 21, Section 101.91.',
        url: 'https://www.fda.gov/food/food-labeling-nutrition/gluten-free-labeling-foods',
      },
      {
        id: 4,
        text: 'Lebwohl B, et al. Long term gluten consumption in adults without celiac disease and risk of coronary heart disease: prospective cohort study. BMJ. 2017;357:j1892.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/28465308/',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 22. Sugar (brief, points to /health/sugar)
  // ─────────────────────────────────────────────
  {
    slug: 'sugar',
    title: 'Sugar',
    synonyms: [
      'added sugar',
      'sucrose',
      'fructose',
      'glucose',
      'high fructose corn syrup',
    ],
    whatItIs:
      'Sugar refers to simple carbohydrates, both naturally occurring (in fruits, dairy) and added (in processed foods and beverages). Added sugar provides calories with no essential nutrients.',
    whyItMatters:
      'Excess added sugar consumption is associated with obesity, type 2 diabetes, cardiovascular disease, non-alcoholic fatty liver disease, dental caries, and accelerated skin aging through glycation. The WHO and AHA recommend limiting added sugar intake (ref 1, 2). For a comprehensive deep dive on sugar, its metabolic effects, hidden sources, and practical reduction strategies, see the full guide at /health/sugar.',
    sources: [
      'Naturally sweet: berries, apples, dates (whole fruit is preferred due to fibre content)',
      'Honey, maple syrup (natural but still added sugars)',
      'Limit: soft drinks, sweets, baked goods, flavoured yoghurts, sauces, cereals',
    ],
    howMuch:
      'The AHA recommends no more than 25 g/day (6 tsp) for women and 36 g/day (9 tsp) for men of added sugars (ref 2). The WHO suggests less than 10% of total energy from free sugars, with additional benefits below 5% (ref 1). See /health/sugar for detailed guidance.',
    deficiencyExcess:
      'There is no dietary requirement for added sugar. Excess consumption drives insulin resistance, visceral fat accumulation, and chronic inflammation. See /health/sugar for full details.',
    ageNotes:
      'Children and older adults are particularly vulnerable to the effects of excess sugar. See /health/sugar for age-specific guidance.',
    myths: [
      {
        myth: 'Natural sugars (honey, agave) are healthy alternatives.',
        fact: 'While minimally processed sweeteners contain trace nutrients, they have similar metabolic effects to refined sugar. The dose matters more than the source (ref 1). More myth-busting at /health/sugar.',
      },
    ],
    relatedTopics: [
      'belly-fat',
      'visceral-fat',
      'inflammation',
      'gut-health',
      'skin-health',
      'metabolism',
      'cholesterol',
    ],
    references: [
      {
        id: 1,
        text: 'WHO. Guideline: Sugars intake for adults and children. Geneva: World Health Organization, 2015.',
        url: 'https://www.who.int/publications/i/item/9789241549028',
      },
      {
        id: 2,
        text: 'Vos MB, et al. Added Sugars and Cardiovascular Disease Risk in Children: A Scientific Statement From the American Heart Association. Circulation. 2017;135(19):e1017-e1034.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/27550974/',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 23. Gut Health
  // ─────────────────────────────────────────────
  {
    slug: 'gut-health',
    title: 'Gut Health',
    synonyms: [
      'microbiome',
      'probiotics',
      'prebiotics',
      'digestive health',
      'gut flora',
      'gut bacteria',
    ],
    whatItIs:
      'Gut health refers to the function and balance of the gastrointestinal tract, particularly the trillions of microorganisms (the gut microbiome) residing in the large intestine. A diverse, balanced microbiome is a marker of good gut health.',
    whyItMatters:
      'The gut microbiome influences digestion, nutrient absorption, immune function (approximately 70% of immune cells reside in the gut), mental health (the gut-brain axis), and metabolic health. Dysbiosis (microbial imbalance) is associated with inflammatory bowel disease, obesity, type 2 diabetes, autoimmune conditions, and depression (ref 1, 2, 3).',
    sources: [
      'Fermented foods: yoghurt, kefir, sauerkraut, kimchi, miso, tempeh, kombucha',
      'Prebiotic fibre: garlic, onions, leeks, asparagus, bananas, oats, Jerusalem artichokes',
      'Polyphenol-rich foods: berries, green tea, dark chocolate, olive oil',
      'High-fibre whole foods: legumes, whole grains, vegetables, fruits',
    ],
    howMuch:
      'There is no single "dose" for gut health. Key principles: eat 30+ different plant foods per week for microbiome diversity (ref 4), include fermented foods daily, consume 25-38 g of fibre per day, and limit ultra-processed foods, artificial sweeteners, and unnecessary antibiotics.',
    deficiencyExcess:
      'Signs of poor gut health include chronic bloating, gas, constipation or diarrhoea, food intolerances, and frequent infections. Over-supplementation with probiotics can cause bloating and SIBO in some individuals. Probiotic benefits are strain-specific.',
    ageNotes:
      'Microbiome diversity naturally declines with age. Older adults benefit from prioritising fibre-rich and fermented foods. Antibiotic use (more common in elderly populations) significantly disrupts the microbiome.',
    myths: [
      {
        myth: 'All probiotics are the same.',
        fact: 'Probiotic effects are highly strain-specific. Lactobacillus rhamnosus GG for diarrhoea has different evidence than a generic probiotic blend. Choose evidence-backed strains for specific conditions (ref 5).',
      },
      {
        myth: 'You need to do a "gut cleanse" or detox.',
        fact: 'The body has its own detoxification systems (liver, kidneys). There is no scientific evidence supporting commercial "gut cleanses." Gradual dietary improvement is more effective and safer (ref 1).',
      },
    ],
    relatedTopics: [
      'fibre',
      'inflammation',
      'sleep',
      'sugar',
      'hydration',
      'metabolism',
      'gluten',
      'anti-aging',
    ],
    references: [
      {
        id: 1,
        text: 'Valdes AM, et al. Role of the gut microbiota in nutrition and health. BMJ. 2018;361:k2179.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/29899036/',
      },
      {
        id: 2,
        text: 'Cryan JF, et al. The Microbiota-Gut-Brain Axis. Physiol Rev. 2019;99(4):1877-2013.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/31460832/',
      },
      {
        id: 3,
        text: 'Zheng D, et al. Interaction between microbiota and immunity in health and disease. Cell Res. 2020;30(6):492-506.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/32433595/',
      },
      {
        id: 4,
        text: 'McDonald D, et al. American Gut: an Open Platform for Citizen Science Microbiome Research. mSystems. 2018;3(3):e00031-18.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/29795809/',
      },
      {
        id: 5,
        text: 'Hill C, et al. Expert consensus document. The International Scientific Association for Probiotics and Prebiotics consensus statement on the scope and appropriate use of the term probiotic. Nat Rev Gastroenterol Hepatol. 2014;11(8):506-514.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/24912386/',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 24. Sleep
  // ─────────────────────────────────────────────
  {
    slug: 'sleep',
    title: 'Sleep',
    synonyms: ['insomnia', 'sleep quality', 'rest', 'recovery', 'circadian rhythm'],
    whatItIs:
      'Sleep is a restorative biological process essential for physical recovery, cognitive function, memory consolidation, and hormonal regulation. It cycles through stages including light sleep, deep (slow-wave) sleep, and REM sleep.',
    whyItMatters:
      'Chronic sleep deprivation (less than 7 hours for most adults) is associated with increased risk of obesity, type 2 diabetes, cardiovascular disease, impaired immunity, cognitive decline, and all-cause mortality. Sleep is when growth hormone peaks, muscle repair occurs, and the brain clears metabolic waste (the glymphatic system) (ref 1, 2, 3).',
    sources: [
      'Tart cherries (natural melatonin)',
      'Kiwi fruit (serotonin, antioxidants)',
      'Fatty fish (vitamin D, omega-3)',
      'Almonds and walnuts (magnesium, melatonin)',
      'Turkey (tryptophan)',
      'Chamomile tea',
      'Warm milk',
      'Bananas (magnesium, tryptophan)',
      'Oats (melatonin precursors)',
    ],
    howMuch:
      'The National Sleep Foundation recommends 7-9 hours per night for adults (18-64), 7-8 hours for older adults (65+) (ref 4). Quality matters as much as quantity: aim for consistent sleep/wake times, adequate deep sleep, and minimal disruptions.',
    deficiencyExcess:
      'Sleep deprivation signs include daytime fatigue, difficulty concentrating, irritability, weakened immunity, and increased appetite (ghrelin rises, leptin falls). Consistently sleeping >9 hours may indicate underlying health issues and has also been associated with increased mortality risk in observational studies (ref 1).',
    ageNotes:
      'Sleep architecture changes with age: less deep sleep, more awakenings, and earlier circadian timing. Older adults still need 7-8 hours but may find it harder to achieve without good sleep hygiene.',
    myths: [
      {
        myth: 'You can "catch up" on sleep over the weekend.',
        fact: 'While recovery sleep can partially restore cognitive function, chronic sleep debt has cumulative metabolic and cardiovascular effects that weekend sleep cannot fully reverse (ref 5).',
      },
      {
        myth: 'A nightcap helps you sleep better.',
        fact: 'Alcohol may help you fall asleep faster but significantly disrupts sleep architecture, reducing REM sleep and causing more awakenings in the second half of the night (ref 1).',
      },
    ],
    relatedTopics: [
      'magnesium',
      'muscle-recovery',
      'hormones',
      'metabolism',
      'inflammation',
      'anti-aging',
      'gut-health',
    ],
    references: [
      {
        id: 1,
        text: 'Walker M. Why We Sleep: Unlocking the Power of Sleep and Dreams. Scribner, 2017. (Note: popular science book; specific claims corroborated by primary sources below.)',
        url: 'https://www.simonandschuster.com/books/Why-We-Sleep/Matthew-Walker/9781501144325',
      },
      {
        id: 2,
        text: 'Xie L, et al. Sleep drives metabolite clearance from the adult brain. Science. 2013;342(6156):373-377.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/24136970/',
      },
      {
        id: 3,
        text: 'Cappuccio FP, et al. Sleep duration and all-cause mortality: a systematic review and meta-analysis of prospective studies. Sleep. 2010;33(5):585-592.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/20469800/',
      },
      {
        id: 4,
        text: 'Hirshkowitz M, et al. National Sleep Foundation\'s sleep time duration recommendations: methodology and results summary. Sleep Health. 2015;1(1):40-43.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/29073412/',
      },
      {
        id: 5,
        text: 'Depner CM, et al. Ad libitum Weekend Recovery Sleep Fails to Prevent Metabolic Dysregulation during a Repeating Pattern of Insufficient Sleep and Weekend Recovery Sleep. Curr Biol. 2019;29(6):957-967.e4.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/30827911/',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 25. Hydration
  // ─────────────────────────────────────────────
  {
    slug: 'hydration',
    title: 'Hydration',
    synonyms: ['water intake', 'dehydration', 'electrolytes', 'fluid intake'],
    whatItIs:
      'Hydration refers to maintaining adequate fluid balance in the body. Water constitutes approximately 60% of adult body weight and is essential for virtually every physiological process, including nutrient transport, temperature regulation, and waste removal.',
    whyItMatters:
      'Even mild dehydration (1-2% body weight loss) can impair cognitive function, mood, and physical performance. Chronic under-hydration is associated with increased risk of kidney stones, urinary tract infections, and constipation. Proper hydration supports joint lubrication, skin health, and cardiovascular function (ref 1, 2).',
    sources: [
      'Water (still or sparkling)',
      'Herbal teas',
      'Water-rich fruits: watermelon, cucumbers, oranges, strawberries',
      'Water-rich vegetables: lettuce, celery, zucchini, tomatoes',
      'Soups and broths',
      'Coconut water (natural electrolytes)',
      'Milk',
    ],
    howMuch:
      'The National Academies recommend approximately 3.7 L/day total water (from all beverages and food) for men and 2.7 L/day for women (ref 3). About 20% typically comes from food. Needs increase with exercise, heat exposure, illness, and pregnancy/lactation. Urine colour (pale yellow) is a practical hydration indicator.',
    deficiencyExcess:
      'Dehydration signs include dark urine, thirst, dry mouth, headache, dizziness, and fatigue. Severe dehydration is a medical emergency. Overhydration (hyponatraemia) from excessive water intake without electrolytes can be dangerous, particularly during endurance exercise (ref 4).',
    ageNotes:
      'Older adults have a diminished thirst sensation and are at higher risk of dehydration. Certain medications (diuretics) increase fluid needs. Regular, scheduled fluid intake is often recommended rather than relying on thirst.',
    myths: [
      {
        myth: 'You need exactly 8 glasses (64 oz) of water per day.',
        fact: 'The "8x8" rule has no strong scientific basis. Fluid needs vary considerably based on body size, activity level, climate, and diet. Total fluid intake (from all sources including food) is what matters (ref 3).',
      },
      {
        myth: 'Coffee and tea dehydrate you.',
        fact: 'While caffeine has a mild diuretic effect, caffeinated beverages still provide a net positive contribution to hydration at typical consumption levels (ref 5).',
      },
    ],
    relatedTopics: [
      'muscle-recovery',
      'skin-health',
      'gut-health',
      'sleep',
      'metabolism',
      'strength-training',
    ],
    references: [
      {
        id: 1,
        text: 'Popkin BM, D\'Anci KE, Rosenberg IH. Water, hydration, and health. Nutr Rev. 2010;68(8):439-458.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/20646222/',
      },
      {
        id: 2,
        text: 'Ganio MS, et al. Mild dehydration impairs cognitive performance and mood of men. Br J Nutr. 2011;106(10):1535-1543.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/21736786/',
      },
      {
        id: 3,
        text: 'National Academies of Sciences. Dietary Reference Intakes for Water, Potassium, Sodium, Chloride, and Sulfate. National Academies Press, 2005.',
        url: 'https://nap.nationalacademies.org/catalog/10925/dietary-reference-intakes-for-water-potassium-sodium-chloride-and-sulfate',
      },
      {
        id: 4,
        text: 'Hew-Butler T, et al. Statement of the Third International Exercise-Associated Hyponatremia Consensus Development Conference. Clin J Sport Med. 2015;25(4):303-320.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/26102445/',
      },
      {
        id: 5,
        text: 'Killer SC, Blannin AK, Jeukendrup AE. No evidence of dehydration with moderate daily coffee intake: a counterbalanced cross-over study in a free-living population. PLoS One. 2014;9(1):e84154.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/24416202/',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 26. Inflammation
  // ─────────────────────────────────────────────
  {
    slug: 'inflammation',
    title: 'Inflammation',
    synonyms: [
      'chronic inflammation',
      'anti-inflammatory',
      'CRP',
      'inflammatory response',
      'systemic inflammation',
    ],
    whatItIs:
      'Inflammation is the immune system\'s response to injury, infection, or irritation. Acute inflammation is protective and resolves quickly, while chronic low-grade inflammation persists and contributes to disease. C-reactive protein (CRP) is a common blood marker for systemic inflammation.',
    whyItMatters:
      'Chronic low-grade inflammation is now recognised as a root driver of most major chronic diseases, including cardiovascular disease, type 2 diabetes, cancer, Alzheimer\'s disease, and autoimmune conditions. Diet, exercise, sleep, and stress are major modifiable factors that influence inflammatory status (ref 1, 2, 3).',
    sources: [
      'Anti-inflammatory: fatty fish, olive oil, berries, leafy greens, turmeric, ginger, nuts, tomatoes, green tea',
      'Pro-inflammatory (limit): refined carbs, fried foods, red/processed meat (in excess), added sugars, trans fats, excessive alcohol',
    ],
    howMuch:
      'There is no single anti-inflammatory "dose." The Mediterranean and DASH diets have the strongest evidence for reducing inflammatory markers. Key targets: omega-3 intake (250-500 mg EPA+DHA/day), 5+ servings of fruits and vegetables daily, and limiting ultra-processed food intake. hs-CRP below 1 mg/L is considered low cardiovascular risk (ref 4).',
    deficiencyExcess:
      'Too little acute inflammation impairs wound healing and pathogen clearance. Chronic excess inflammation drives tissue damage, accelerated aging, and disease progression. The goal is a well-regulated inflammatory response, not total suppression.',
    ageNotes:
      '"Inflammaging" -- the age-related increase in chronic low-grade inflammation -- is a hallmark of aging and a key target for longevity interventions (ref 5).',
    myths: [
      {
        myth: 'All inflammation is bad.',
        fact: 'Acute inflammation is essential for healing and immune defence. It is chronic, unresolved inflammation that drives disease. Anti-inflammatory strategies target the chronic form (ref 1).',
      },
      {
        myth: 'Anti-inflammatory supplements can replace a healthy diet.',
        fact: 'While curcumin, omega-3, and other supplements show anti-inflammatory effects in trials, a whole-diet approach is more effective than any single supplement (ref 3).',
      },
    ],
    relatedTopics: [
      'omega-3',
      'gut-health',
      'sugar',
      'sleep',
      'anti-aging',
      'belly-fat',
      'visceral-fat',
      'cholesterol',
    ],
    references: [
      {
        id: 1,
        text: 'Furman D, et al. Chronic inflammation in the etiology of disease across the life span. Nat Med. 2019;25(12):1822-1832.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/31806905/',
      },
      {
        id: 2,
        text: 'Libby P. Inflammation in atherosclerosis. Arterioscler Thromb Vasc Biol. 2012;32(9):2045-2051.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/22895665/',
      },
      {
        id: 3,
        text: 'Galland L. Diet and inflammation. Nutr Clin Pract. 2010;25(6):634-640.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/21139128/',
      },
      {
        id: 4,
        text: 'Ridker PM. C-Reactive Protein: Eighty Years from Discovery to Emergence as a Major Risk Marker for Cardiovascular Disease. Clin Chem. 2009;55(2):209-215.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/19095723/',
      },
      {
        id: 5,
        text: 'Franceschi C, et al. Inflammaging: a new immune-metabolic viewpoint for age-related diseases. Nat Rev Endocrinol. 2018;14(10):576-590.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/30046148/',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 27. Cholesterol
  // ─────────────────────────────────────────────
  {
    slug: 'cholesterol',
    title: 'Cholesterol',
    synonyms: ['LDL', 'HDL', 'triglycerides', 'lipid panel', 'dyslipidemia'],
    whatItIs:
      'Cholesterol is a waxy, fat-like substance produced by the liver and obtained from animal foods. It is essential for cell membranes, hormone production, and vitamin D synthesis. It travels in the blood via lipoproteins: LDL ("bad"), HDL ("good"), and VLDL (carries triglycerides).',
    whyItMatters:
      'Elevated LDL cholesterol is a causal factor in atherosclerosis and cardiovascular disease, the leading cause of death globally. High triglycerides and low HDL further increase risk. Dietary pattern, physical activity, genetics, and body weight all influence lipid levels (ref 1, 2).',
    sources: [
      'LDL-lowering foods: oats, barley, legumes, nuts (almonds, walnuts), fatty fish, olive oil, soy protein, plant sterols/stanols, avocados',
      'Foods that raise LDL (limit): trans fats (partially hydrogenated oils), excessive saturated fat, ultra-processed foods',
    ],
    howMuch:
      'Desirable levels: total cholesterol <200 mg/dL, LDL <100 mg/dL (lower for high-risk individuals), HDL >40 mg/dL (men) or >50 mg/dL (women), triglycerides <150 mg/dL (ref 3). The AHA recommends limiting saturated fat to <6% of calories for those needing to lower LDL (ref 2).',
    deficiencyExcess:
      'Very low cholesterol (total <150 mg/dL) has been associated with increased haemorrhagic stroke risk in some studies, though this remains debated [VERIFY]. Elevated LDL (>160 mg/dL) and triglycerides (>200 mg/dL) significantly increase cardiovascular risk.',
    ageNotes:
      'Cholesterol levels tend to rise with age. Post-menopausal women often see LDL increases due to declining oestrogen. Lipid panels should be checked regularly starting at age 20 per AHA guidelines.',
    myths: [
      {
        myth: 'Eating eggs raises your cholesterol dangerously.',
        fact: 'Dietary cholesterol has a modest effect on blood cholesterol for most people. Saturated and trans fats are the primary dietary drivers of LDL. Current guidelines no longer set a specific dietary cholesterol limit, though moderation is advised (ref 4).',
      },
      {
        myth: 'High HDL always protects you.',
        fact: 'While population studies associate higher HDL with lower risk, drugs that raise HDL have not shown cardiovascular benefit. HDL functionality, not just level, appears to matter (ref 5).',
      },
    ],
    relatedTopics: [
      'good-fats',
      'omega-3',
      'fibre',
      'inflammation',
      'belly-fat',
      'visceral-fat',
      'metabolism',
    ],
    references: [
      {
        id: 1,
        text: 'Ference BA, et al. Low-density lipoproteins cause atherosclerotic cardiovascular disease. 1. Evidence from genetic, epidemiologic, and clinical studies. Eur Heart J. 2017;38(32):2459-2472.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/28444290/',
      },
      {
        id: 2,
        text: 'Sacks FM, et al. Dietary Fats and Cardiovascular Disease: A Presidential Advisory From the American Heart Association. Circulation. 2017;136(3):e1-e23.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/28620111/',
      },
      {
        id: 3,
        text: 'Grundy SM, et al. 2018 AHA/ACC/AACVPR/AAPA/ABC/ACPM/ADA/AGS/APhA/ASPC/NLA/PCNA Guideline on the Management of Blood Cholesterol. J Am Coll Cardiol. 2019;73(24):e285-e350.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/30423393/',
      },
      {
        id: 4,
        text: 'Blesso CN, Fernandez ML. Dietary Cholesterol, Serum Lipids, and Heart Disease: Are Eggs Working for or Against You? Nutrients. 2018;10(4):426.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/29596318/',
      },
      {
        id: 5,
        text: 'Rader DJ, Hovingh GK. HDL and cardiovascular disease. Lancet. 2014;384(9943):618-625.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/25131981/',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 28. Protein Timing
  // ─────────────────────────────────────────────
  {
    slug: 'protein-timing',
    title: 'Protein Timing',
    synonyms: ['nutrient timing', 'anabolic window', 'meal timing', 'post-workout nutrition'],
    whatItIs:
      'Protein timing refers to the strategic distribution of protein intake throughout the day and around exercise to optimise muscle protein synthesis (MPS), recovery, and body composition.',
    whyItMatters:
      'Research suggests that distributing protein intake evenly across meals (rather than concentrating it at dinner) may better stimulate muscle protein synthesis throughout the day. The "anabolic window" post-exercise exists but is wider than commonly believed, particularly when pre-exercise nutrition is adequate (ref 1, 2).',
    sources: [
      'Fast-digesting: whey protein, eggs, Greek yoghurt',
      'Slow-digesting: casein, cottage cheese, meat',
      'Plant-based: soy protein, pea protein, tofu',
      'Whole-food meals containing 20-40 g protein',
    ],
    howMuch:
      'Evidence suggests consuming 0.4-0.55 g/kg of protein per meal, spread across 3-4 meals per day, is a practical target for maximising MPS (ref 2). Post-exercise protein (20-40 g) within approximately 2 hours of training is beneficial, though total daily intake is more important than precise timing (ref 1, 3).',
    deficiencyExcess:
      'Skewing protein heavily towards one meal (common pattern: low protein at breakfast, high at dinner) may result in suboptimal MPS stimulation throughout the day. However, overall daily intake remains the primary factor for muscle outcomes.',
    ageNotes:
      'Older adults may have a higher "leucine threshold" for stimulating MPS and may benefit from slightly higher per-meal protein doses (30-40 g) and including leucine-rich sources at each meal (ref 4).',
    myths: [
      {
        myth: 'You must eat protein within 30 minutes of training or you lose gains.',
        fact: 'The "anabolic window" is much wider than 30 minutes. A 2013 meta-analysis found that total daily protein intake was a stronger predictor of muscle outcomes than timing around exercise (ref 1).',
      },
      {
        myth: 'Eating before bed wastes protein.',
        fact: 'Pre-sleep casein protein (30-40 g) has been shown to increase overnight muscle protein synthesis without increasing fat storage (ref 5).',
      },
    ],
    relatedTopics: [
      'protein',
      'muscle-recovery',
      'strength-training',
      'metabolism',
    ],
    references: [
      {
        id: 1,
        text: 'Schoenfeld BJ, Aragon AA, Krieger JW. The effect of protein timing on muscle strength and hypertrophy: a meta-analysis. J Int Soc Sports Nutr. 2013;10(1):53.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/24299050/',
      },
      {
        id: 2,
        text: 'Schoenfeld BJ, Aragon AA. How much protein can the body use in a single meal for muscle-building? Implications for daily protein distribution. J Int Soc Sports Nutr. 2018;15:10.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/29497353/',
      },
      {
        id: 3,
        text: 'Jager R, et al. International Society of Sports Nutrition Position Stand: protein and exercise. J Int Soc Sports Nutr. 2017;14:20.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/28642676/',
      },
      {
        id: 4,
        text: 'Moore DR, et al. Protein ingestion to stimulate myofibrillar protein synthesis requires greater relative protein intakes in healthy older versus younger men. J Gerontol A Biol Sci Med Sci. 2015;70(1):57-62.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/25056502/',
      },
      {
        id: 5,
        text: 'Res PT, et al. Protein ingestion before sleep improves postexercise overnight recovery. Med Sci Sports Exerc. 2012;44(8):1560-1569.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/22330017/',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 29. Muscle Recovery
  // ─────────────────────────────────────────────
  {
    slug: 'muscle-recovery',
    title: 'Muscle Recovery',
    synonyms: ['DOMS', 'muscle repair', 'rest days', 'recovery', 'muscle soreness'],
    whatItIs:
      'Muscle recovery is the process by which damaged muscle fibres repair and rebuild after exercise, resulting in adaptation (increased strength, size, or endurance). Delayed onset muscle soreness (DOMS) typically peaks 24-72 hours after intense or novel exercise.',
    whyItMatters:
      'Training provides the stimulus, but adaptation occurs during recovery. Inadequate recovery leads to overtraining syndrome, increased injury risk, impaired immune function, and declining performance. Nutrition, sleep, and active recovery are the primary modifiable recovery factors (ref 1, 2).',
    sources: [
      'Protein-rich foods (essential amino acids for muscle repair)',
      'Tart cherry juice (anthocyanins reduce DOMS, ref 3)',
      'Omega-3-rich foods (anti-inflammatory)',
      'Carbohydrate sources (glycogen replenishment)',
      'Anti-inflammatory foods (berries, turmeric)',
      'Magnesium-rich foods (muscle relaxation)',
      'Adequate water (hydration)',
    ],
    howMuch:
      'Post-exercise: consume 20-40 g protein and adequate carbohydrates within a few hours of training. Sleep 7-9 hours per night. Allow 48-72 hours before training the same muscle group intensely again. Active recovery (light walking, stretching, foam rolling) can be done daily (ref 2, 4).',
    deficiencyExcess:
      'Insufficient recovery manifests as persistent soreness, declining performance, elevated resting heart rate, poor sleep, mood disturbances, and frequent illness. Excessive rest (>5-7 days of inactivity) begins to cause detraining effects.',
    ageNotes:
      'Older adults generally require longer recovery periods between intense sessions. Prioritising sleep, protein intake, and anti-inflammatory nutrition becomes increasingly important with age.',
    myths: [
      {
        myth: 'Ice baths accelerate recovery.',
        fact: 'Cold water immersion may reduce perceived soreness but can blunt the adaptive response to strength training (reduced hypertrophy and strength gains). It may be appropriate for athletes needing to perform again quickly but is not ideal for long-term adaptation (ref 5).',
      },
      {
        myth: 'No pain, no gain.',
        fact: 'Some discomfort during training is normal, but sharp pain, joint pain, or soreness lasting beyond 72 hours may indicate injury or overtraining, not productive adaptation (ref 1).',
      },
    ],
    relatedTopics: [
      'protein',
      'protein-timing',
      'sleep',
      'strength-training',
      'magnesium',
      'hydration',
      'inflammation',
    ],
    references: [
      {
        id: 1,
        text: 'Dupuy O, et al. An Evidence-Based Approach for Choosing Post-exercise Recovery Techniques to Reduce Markers of Muscle Damage, Soreness, Fatigue, and Inflammation: A Systematic Review With Meta-Analysis. Front Physiol. 2018;9:403.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/29755363/',
      },
      {
        id: 2,
        text: 'Dattilo M, et al. Sleep and muscle recovery: endocrinological and molecular basis for a new and promising hypothesis. Med Hypotheses. 2011;77(2):220-222.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/21550729/',
      },
      {
        id: 3,
        text: 'Vitale KC, et al. Tart Cherry Juice in Athletes: A Literature Review and Commentary. Curr Sports Med Rep. 2017;16(4):230-239.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/28696985/',
      },
      {
        id: 4,
        text: 'Jager R, et al. International Society of Sports Nutrition Position Stand: protein and exercise. J Int Soc Sports Nutr. 2017;14:20.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/28642676/',
      },
      {
        id: 5,
        text: 'Roberts LA, et al. Post-exercise cold water immersion attenuates acute anabolic signalling and long-term adaptations in muscle to strength training. J Physiol. 2015;593(18):4285-4301.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/26174323/',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 30. Bone Density
  // ─────────────────────────────────────────────
  {
    slug: 'bone-density',
    title: 'Bone Density',
    synonyms: ['osteoporosis', 'bone health', 'bone mass', 'DEXA', 'bone mineral density'],
    whatItIs:
      'Bone density (bone mineral density, BMD) is a measure of the amount of mineral matter per square centimetre of bone. It reflects bone strength and fracture risk. Peak bone mass is typically achieved by age 30, after which gradual loss begins.',
    whyItMatters:
      'Osteoporosis (severely low bone density) affects an estimated 200 million people worldwide and is a leading cause of fractures in older adults, particularly hip fractures which carry significant morbidity and mortality. Building strong bones early in life and minimising loss later are both critical (ref 1, 2).',
    sources: [
      'Calcium-rich foods: dairy, sardines, tofu, leafy greens',
      'Vitamin D sources: sunlight, fatty fish, fortified foods',
      'Vitamin K-rich foods: leafy greens, natto',
      'Magnesium-rich foods: nuts, seeds, whole grains',
      'Protein (adequate, not excessive)',
      'Prunes (emerging evidence for bone health, ref 3)',
    ],
    howMuch:
      'Key nutrient targets: calcium 1,000-1,200 mg/day, vitamin D 600-800 IU/day (many experts recommend higher), adequate protein (1.0-1.2 g/kg for older adults). Weight-bearing and resistance exercise are as important as nutrition. DEXA scans are recommended for women 65+ and men 70+ (or earlier with risk factors) (ref 4).',
    deficiencyExcess:
      'Low bone density (osteopenia: T-score -1.0 to -2.5; osteoporosis: T-score below -2.5) increases fracture risk. Very high calcium intake (>2,000-2,500 mg/day) has been associated with kidney stones and possibly cardiovascular risk.',
    ageNotes:
      'Women lose bone rapidly in the 5-7 years following menopause due to oestrogen decline. Men experience more gradual loss. Weight-bearing exercise, fall prevention, and adequate calcium/vitamin D are cornerstones of age-related bone health management.',
    myths: [
      {
        myth: 'Only older women get osteoporosis.',
        fact: 'While post-menopausal women are at highest risk, men account for approximately 20% of osteoporosis cases. It can also affect younger people with certain medical conditions or medication use (ref 1).',
      },
      {
        myth: 'Weight-bearing exercise is not safe with low bone density.',
        fact: 'Weight-bearing and resistance exercise are actually recommended for people with osteoporosis (with appropriate modifications). They help maintain bone density and reduce fall risk (ref 5).',
      },
    ],
    relatedTopics: [
      'calcium',
      'vitamin-k',
      'strength-training',
      'protein',
      'magnesium',
      'hormones',
      'anti-aging',
    ],
    references: [
      {
        id: 1,
        text: 'Compston JE, McClung MR, Leslie WD. Osteoporosis. Lancet. 2019;393(10169):364-376.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/30696576/',
      },
      {
        id: 2,
        text: 'NIH Osteoporosis and Related Bone Diseases National Resource Center. Osteoporosis Overview.',
        url: 'https://www.bones.nih.gov/health-info/bone/osteoporosis/overview',
      },
      {
        id: 3,
        text: 'De Souza MJ, et al. Dried plums (prunes) and bone health: A comprehensive review. Nutrients. 2022;14(12):2538.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/35745261/',
      },
      {
        id: 4,
        text: 'US Preventive Services Task Force. Screening for Osteoporosis: US Preventive Services Task Force Recommendation Statement. JAMA. 2018;319(24):2521-2531.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/29946735/',
      },
      {
        id: 5,
        text: 'Howe TE, et al. Exercise for preventing and treating osteoporosis in postmenopausal women. Cochrane Database Syst Rev. 2011;(7):CD000333.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/21735380/',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 31. Hormones
  // ─────────────────────────────────────────────
  {
    slug: 'hormones',
    title: 'Hormones',
    synonyms: [
      'testosterone',
      'estrogen',
      'oestrogen',
      'cortisol',
      'insulin',
      'thyroid',
      'hormonal health',
    ],
    whatItIs:
      'Hormones are chemical messengers produced by endocrine glands (thyroid, adrenals, gonads, pancreas, pituitary) that regulate virtually every bodily process, including metabolism, reproduction, mood, growth, and stress response.',
    whyItMatters:
      'Hormonal imbalances can profoundly affect body composition, energy, mood, sleep, libido, and disease risk. Lifestyle factors -- diet, exercise, sleep, and stress -- have significant, well-documented effects on hormonal profiles. Understanding key hormones helps contextualise many health topics (ref 1, 2).',
    sources: [
      'Cruciferous vegetables (support oestrogen metabolism)',
      'Zinc-rich foods: oysters, beef, pumpkin seeds (testosterone support)',
      'Healthy fats (hormone precursors): olive oil, avocados, nuts',
      'Fibre-rich foods (help regulate insulin and oestrogen)',
      'Selenium-rich foods: Brazil nuts, fish (thyroid support)',
      'Vitamin D (functions as a hormone)',
      'Magnesium-rich foods (cortisol regulation)',
    ],
    howMuch:
      'There is no universal "hormone-balancing diet." Key evidence-based strategies: maintain a healthy body weight (excess body fat increases oestrogen and insulin), eat adequate protein (supports anabolic hormones), manage stress (reduces chronic cortisol elevation), prioritise sleep (growth hormone peaks during deep sleep), and exercise regularly (improves insulin sensitivity and testosterone) (ref 2, 3).',
    deficiencyExcess:
      'Low testosterone: fatigue, muscle loss, low libido, depression. High cortisol: weight gain (especially visceral), anxiety, impaired immunity. Insulin resistance: weight gain, fatigue, pre-diabetes. Thyroid dysfunction: metabolism changes, temperature sensitivity, fatigue. Diagnosis requires blood testing.',
    ageNotes:
      'Testosterone declines approximately 1-2% per year after age 30 in men [VERIFY]. Women experience dramatic hormonal shifts during perimenopause and menopause (typically ages 45-55). Thyroid issues become more common with age.',
    myths: [
      {
        myth: 'Testosterone therapy is a fountain of youth for all aging men.',
        fact: 'Testosterone replacement is appropriate for clinically diagnosed hypogonadism, not normal aging. It carries risks including cardiovascular events, polycythaemia, and potential prostate concerns (ref 4).',
      },
      {
        myth: 'Soy raises oestrogen levels in men.',
        fact: 'Clinical evidence does not support that normal soy consumption affects oestrogen levels or testosterone in men. Phytoestrogens in soy have very weak oestrogenic activity (ref 5).',
      },
    ],
    relatedTopics: [
      'sleep',
      'strength-training',
      'belly-fat',
      'visceral-fat',
      'metabolism',
      'bone-density',
      'hair-health',
      'anti-aging',
    ],
    references: [
      {
        id: 1,
        text: 'Hackney AC. Stress and the neuroendocrine system: the role of exercise as a stressor and modifier of stress. Expert Rev Endocrinol Metab. 2006;1(6):783-792.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/20948580/',
      },
      {
        id: 2,
        text: 'Leproult R, Van Cauter E. Effect of 1 week of sleep restriction on testosterone levels in young healthy men. JAMA. 2011;305(21):2173-2174.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/21632481/',
      },
      {
        id: 3,
        text: 'Kraemer WJ, Ratamess NA. Hormonal responses and adaptations to resistance exercise and training. Sports Med. 2005;35(4):339-361.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/15831061/',
      },
      {
        id: 4,
        text: 'Bhasin S, et al. Testosterone Therapy in Men With Hypogonadism: An Endocrine Society Clinical Practice Guideline. J Clin Endocrinol Metab. 2018;103(5):1715-1744.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/29562364/',
      },
      {
        id: 5,
        text: 'Hamilton-Reeves JM, et al. Clinical studies show no effects of soy protein or isoflavones on reproductive hormones in men: results of a meta-analysis. Fertil Steril. 2010;94(3):997-1007.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/19524224/',
      },
    ],
  },

  // ─────────────────────────────────────────────
  // 32. Metabolism
  // ─────────────────────────────────────────────
  {
    slug: 'metabolism',
    title: 'Metabolism',
    synonyms: ['metabolic rate', 'BMR', 'TDEE', 'thermogenesis', 'metabolic health'],
    whatItIs:
      'Metabolism encompasses all chemical reactions in the body that convert food into energy and building blocks for growth and repair. Basal metabolic rate (BMR) is the energy expended at rest; total daily energy expenditure (TDEE) includes BMR, physical activity, and the thermic effect of food.',
    whyItMatters:
      'Understanding metabolism helps explain energy balance, weight management, and metabolic health. Metabolic syndrome (a cluster of conditions: abdominal obesity, high blood sugar, high triglycerides, low HDL, and hypertension) affects an estimated 25-30% of adults and significantly increases cardiovascular and diabetes risk (ref 1, 2).',
    sources: [
      'Protein-rich foods (highest thermic effect: 20-30% of calories)',
      'Green tea and coffee (modest metabolic boost from caffeine and catechins)',
      'Spicy foods (capsaicin has a small thermogenic effect)',
      'Cold water (minor increase in metabolic rate)',
      'High-fibre whole foods',
      'Iron, zinc, and selenium-rich foods (thyroid function)',
    ],
    howMuch:
      'BMR varies by age, sex, weight, height, and body composition. Average adult BMR is approximately 1,400-1,800 kcal/day [VERIFY range]. TDEE = BMR x activity factor (1.2-1.9). For weight loss, a moderate caloric deficit of 500 kcal/day below TDEE is generally recommended for approximately 0.5 kg (1 lb) of fat loss per week (ref 3). Metabolic adaptation (reduced metabolic rate during dieting) is a real phenomenon to consider.',
    deficiencyExcess:
      'A very low metabolic rate can indicate hypothyroidism or excessive caloric restriction (adaptive thermogenesis). Metabolic syndrome components should be monitored: fasting glucose >100 mg/dL, waist circumference, triglycerides >150 mg/dL, HDL <40/50 mg/dL (men/women), blood pressure >130/85 mmHg (ref 4).',
    ageNotes:
      'Metabolic rate declines approximately 1-2% per decade after age 20, largely due to loss of lean muscle mass. A 2021 study in Science found that metabolism is relatively stable from age 20-60 and declines more meaningfully after 60 (ref 5). Resistance training is the most effective intervention for preserving metabolic rate with age.',
    myths: [
      {
        myth: 'Eating small, frequent meals boosts metabolism.',
        fact: 'The thermic effect of food is proportional to total caloric intake, not meal frequency. Studies comparing meal frequencies with matched total calories show no significant difference in metabolic rate or fat loss (ref 6).',
      },
      {
        myth: 'Some people have a "broken" metabolism.',
        fact: 'While metabolic rates vary between individuals (by approximately 200-300 kcal/day for similar body sizes), clinically "broken" metabolisms are rare. Adaptive thermogenesis from prolonged dieting is real but reversible with appropriate refeeding and resistance training (ref 2).',
      },
      {
        myth: 'Starvation mode prevents all weight loss.',
        fact: 'Metabolic adaptation can slow weight loss by reducing energy expenditure by 10-15% beyond what is predicted by weight loss alone, but it does not halt loss entirely when a caloric deficit is maintained (ref 3).',
      },
    ],
    relatedTopics: [
      'strength-training',
      'protein',
      'hormones',
      'belly-fat',
      'visceral-fat',
      'sleep',
      'inflammation',
      'cholesterol',
    ],
    references: [
      {
        id: 1,
        text: 'Grundy SM, et al. Diagnosis and management of the metabolic syndrome: an American Heart Association/National Heart, Lung, and Blood Institute Scientific Statement. Circulation. 2005;112(17):2735-2752.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/16157765/',
      },
      {
        id: 2,
        text: 'Muller MJ, et al. Metabolic adaptation to caloric restriction and subsequent refeeding: the Minnesota Starvation Experiment revisited. Am J Clin Nutr. 2015;102(4):807-819.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/26399868/',
      },
      {
        id: 3,
        text: 'Hall KD, et al. Energy balance and its components: implications for body weight regulation. Am J Clin Nutr. 2012;95(4):989-994.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/22434603/',
      },
      {
        id: 4,
        text: 'Alberti KG, et al. Harmonizing the metabolic syndrome. Circulation. 2009;120(16):1640-1645.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/19805654/',
      },
      {
        id: 5,
        text: 'Pontzer H, et al. Daily energy expenditure through the human life course. Science. 2021;373(6556):808-812.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/34385400/',
      },
      {
        id: 6,
        text: 'Schoenfeld BJ, Aragon AA, Krieger JW. Effects of meal frequency on weight loss and body composition: a meta-analysis. Nutr Rev. 2015;73(2):69-82.',
        url: 'https://pubmed.ncbi.nlm.nih.gov/26024494/',
      },
    ],
  },
];

export default foodTopics;
